import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { GoogleMap, Polyline, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import RoutePolyline from '../../components/maps/RoutePolyline';
import { getGoogleApiFlag, trackGoogleApiUsage } from '../../hooks/useGoogleApiFlags';
import { MapPin, User, XCircle, ArrowsClockwise, X, Clock, Path, GasPump, Camera, Flag, Play, Pause, ArrowCounterClockwise, CaretLeft, CaretRight, MapTrifold } from '@phosphor-icons/react';
import ManualBadge from '../../components/ManualBadge';
import ExportButton from '../../components/ExportButton';
import SupplierLocationPicker from '../../components/SupplierLocationPicker';
import { useAuth } from '../../contexts/AuthContext';
import LiveTripMapModal from './LiveTripMapModal';
import AllocateConfirmModal from '../../components/AllocateConfirmModal';
import SortableExtraStops from '../../components/SortableExtraStops';
import RoutePreviewModal from '../../components/RoutePreviewModal';
import SupplierSearchSelect from '../../components/SupplierSearchSelect';

// Trip statuses that represent a "live, on-the-road" trip. These rows
// get the green ⊕ Map icon in Trip Monitoring so admin can pop the
// Live Map without opening the full trip detail panel.
const LIVE_TRIP_STATUSES = new Set(['in_progress', 'pickup_reached', 'pickup_done', 'drop_reached']);

// Phase 78ax — Expanded trip-monitoring CSV export. Lists EVERY field
// admins have been asking for on the table. Kept in display order
// (header → flow → people → status → all timestamps → KMs → items →
// helpers → extras → audit) so the spreadsheet is human-readable.
const fmtDateTime = r => r ? new Date(r).toLocaleString('en-IN', { hour12: true, timeZone: 'Asia/Kolkata' }) : '';
const TRIP_EXPORT_COLUMNS = [
  // Identifiers
  { key: 'id', label: 'Trip ID' },
  { key: 'booking_id', label: 'Booking ID' },
  // Locations
  { key: 'pickup_supplier_name', label: 'Pickup' },
  { key: 'pickup_location', label: 'Pickup Address' },
  { key: 'pickup_lat', label: 'Pickup Lat' },
  { key: 'pickup_lng', label: 'Pickup Lng' },
  { key: 'drop_supplier_name', label: 'Drop' },
  { key: 'drop_location', label: 'Drop Address' },
  { key: 'drop_lat', label: 'Drop Lat' },
  { key: 'drop_lng', label: 'Drop Lng' },
  // Vehicle types
  { key: 'vehicle_type_1', label: 'Vehicle Type 1' },
  { key: 'vehicle_type_2', label: 'Vehicle Type 2' },
  // People
  { key: 'pilot_name', label: 'Pilot' },
  { key: 'pilot_mobile', label: 'Pilot Mobile' },
  { key: 'vehicle_number', label: 'Vehicle' },
  { key: 'booked_by_name', label: 'Booked By' },
  { key: 'booked_by_mobile', label: 'Booker Mobile' },
  { key: 'booked_from_halt', label: 'Booking From' },
  { key: 'manual_allocated_by', label: 'Manually Allocated By' },
  { key: 'preferred_pilot_name', label: 'Preferred Pilot' },
  // Pickup-flow / DC
  { key: 'pickup_flow', label: 'Pickup Flow', format: r => r.pickup_flow ? 'Yes' : 'No' },
  { key: 'pickup_user_name', label: 'Pickup User' },
  { key: 'pickup_user_mobile', label: 'Pickup Mobile' },
  { key: 'dc_ready', label: 'DC Ready', format: r => r.dc_ready ? 'Yes' : 'No' },
  { key: 'dc_ready_at', label: 'DC Ready At', format: r => fmtDateTime(r.dc_ready_at) },
  { key: 'dc_photo', label: 'DC Photo URL' },
  { key: 'awaiting_goods_ready', label: 'Awaiting Goods', format: r => r.awaiting_goods_ready ? 'Yes' : 'No' },
  { key: 'material_ready_datetime', label: 'Material Ready At', format: r => fmtDateTime(r.material_ready_datetime) },
  // Lifecycle / status
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'queue_position', label: 'Queue#' },
  { key: 'allocation_mode', label: 'Allocation Mode', format: r => r.allocation_mode || (r.allocation_type === 'manual' || r.preferred_pilot_name ? 'manual' : 'auto') },
  { key: 'allocation_type', label: 'Allocation Type' },
  { key: 'pool_id', label: 'Pool ID' },
  { key: 'is_pooled_into', label: 'Pooled Into' },
  // MANUAL (button-tap) timestamps
  { key: 'created_at', label: 'Created At', format: r => fmtDateTime(r.created_at) },
  { key: 'allocated_at', label: 'Allocated At', format: r => fmtDateTime(r.allocated_at) },
  { key: 'accepted_at', label: 'Accepted At', format: r => fmtDateTime(r.accepted_at) },
  { key: 'started_at', label: 'Trip Started At', format: r => fmtDateTime(r.started_at) },
  { key: 'pickup_reached_at', label: 'Pickup Reached', format: r => fmtDateTime(r.pickup_reached_at) },
  { key: 'pickup_done_at', label: 'Pickup Done', format: r => fmtDateTime(r.pickup_done_at) },
  { key: 'drop_reached_at', label: 'Drop Reached', format: r => fmtDateTime(r.drop_reached_at) },
  { key: 'drop_done_at', label: 'Drop Done', format: r => fmtDateTime(r.drop_done_at) },
  { key: 'completed_at', label: 'Completed At', format: r => fmtDateTime(r.completed_at) },
  { key: 'cancelled_at', label: 'Cancelled At', format: r => fmtDateTime(r.cancelled_at) },
  { key: 'cancelled_by', label: 'Cancelled By' },
  { key: 'cancellation_reason', label: 'Cancellation Reason' },
  // GOOGLE (geofence-derived) timestamps
  { key: 'google_start_time', label: 'Google Start', format: r => fmtDateTime(r.google_start_time) },
  { key: 'google_pickup_reach_time', label: 'Google Pickup Reach', format: r => fmtDateTime(r.google_pickup_reach_time) },
  { key: 'google_pickup_complete_time', label: 'Google Pickup Done', format: r => fmtDateTime(r.google_pickup_complete_time) },
  { key: 'google_drop_reach_time', label: 'Google Drop Reach', format: r => fmtDateTime(r.google_drop_reach_time) },
  { key: 'google_drop_complete_time', label: 'Google Drop Done', format: r => fmtDateTime(r.google_drop_complete_time) },
  // Derived deltas (minutes) — set by `_recompute_deltas` in geofence_timings.py
  { key: 'pickup_reach_to_done_min', label: 'Pickup Wait (min)' },
  { key: 'drop_reach_to_done_min', label: 'Drop Wait (min)' },
  { key: 'start_to_pickup_reach_min', label: 'Start→Pickup Reach (min)' },
  { key: 'pickup_done_to_drop_reach_min', label: 'PU Done→Drop Reach (min)' },
  // KMs
  { key: 'start_km', label: 'Start KM' },
  { key: 'end_km', label: 'End KM' },
  { key: 'manual_kms', label: 'Manual KMs' },
  { key: 'google_kms', label: 'Google KMs' },
  { key: 'traveled_km', label: 'Traveled KM' },
  // Goods
  { key: 'items', label: 'Items', format: r => Array.isArray(r.items) ? r.items.map(i => `${i.name}(${i.qty}${i.uom || ''})`).join('; ') : (r.item || '') },
  { key: 'qty', label: 'Qty' },
  { key: 'uom', label: 'UOM' },
  { key: 'remarks', label: 'Remarks' },
  // Helpers / loadmen
  { key: 'loadmen_count', label: 'Helpers Count', format: r => Array.isArray(r.loadmen) ? r.loadmen.length : (r.loadmen_count || 0) },
  { key: 'loadmen_names', label: 'Helpers', format: r => Array.isArray(r.loadmen) ? r.loadmen.map(l => `${l.name}${l.emp_id ? ` (${l.emp_id})` : ''}`).join('; ') : '' },
  // Multi-stop (extras)
  { key: 'extra_pickups_count', label: 'Extra Pickup Stops', format: r => Array.isArray(r.extra_pickups) ? r.extra_pickups.length : 0 },
  { key: 'extra_pickups', label: 'Extra Pickup Names', format: r => Array.isArray(r.extra_pickups) ? r.extra_pickups.map(s => s.supplier_name).join('; ') : '' },
  { key: 'extra_drops_count', label: 'Extra Drop Stops', format: r => Array.isArray(r.extra_drops) ? r.extra_drops.length : 0 },
  { key: 'extra_drops', label: 'Extra Drop Names', format: r => Array.isArray(r.extra_drops) ? r.extra_drops.map(s => s.supplier_name).join('; ') : '' },
  // Delay reasons (captured by Phase 78b delay-reason dialog)
  { key: 'pickup_delay_reason', label: 'Pickup Delay Reason' },
  { key: 'drop_delay_reason', label: 'Drop Delay Reason' },
];

const STATUS_BADGE = {
  awaiting_goods_ready: 'bg-[#A855F7]/10 text-[#A855F7]',
  pending: 'bg-[#808080]/10 text-[#808080]', allocated: 'bg-[#002FA7]/10 text-[#002FA7]',
  accepted: 'bg-[#00C853]/10 text-[#00C853]', in_progress: 'bg-[#002FA7]/10 text-[#002FA7]',
  pickup_reached: 'bg-[#FFD700]/10 text-[#B8860B]', pickup_done: 'bg-[#FF8C00]/10 text-[#FF8C00]',
  drop_reached: 'bg-[#FFD700]/10 text-[#B8860B]', completed: 'bg-[#00C853]/10 text-[#00C853]',
  cancelled: 'bg-[#FF3333]/10 text-[#FF3333]',
  pooled: 'bg-[#A855F7]/10 text-[#A855F7]',
};

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

function fmtTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
}
function fmtAgo(iso) {
  if (!iso) return '';
  const mins = (Date.now() - new Date(iso)) / 60000;
  if (mins < 1) return 'just now';
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m ago`;
  return `${Math.floor(mins / 60 / 24)}d ago`;
}
function fmtDuration(start, end) {
  if (!start || !end) return '-';
  const mins = (new Date(end) - new Date(start)) / 60000;
  if (mins < 60) return `${Math.round(mins)} min`;
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
}

// ===== TRIP DETAIL PANEL =====
function TripDetailPanel({ tripId, onClose, currentUser }) {
  const { isLoaded } = useGoogleMaps();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [directions, setDirections] = useState(null);
  const [directionsRequested, setDirectionsRequested] = useState(false);
  // Supplier-location picker (admin can pin from Trip Monitoring per Phase-65 design)
  const [locDialog, setLocDialog] = useState(null); // { side, supplier }
  // Route Replay state (admin-only feature for completed trips)
  const [replayIndex, setReplayIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 2x, 4x
  // Click-to-enlarge: holds the index (within `panelPhotos`) of the photo
  // currently shown in the lightbox; null = closed.
  const [zoomedIndex, setZoomedIndex] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  // Phase 78ag — Admin Add/Insert Extra Stop dialog state.
  const [stopDialog, setStopDialog] = useState(null); // { side }
  const [stopForm, setStopForm] = useState({ supplier_name: '', supplier_id: '', supplier_lat: 0, supplier_lng: 0, item: '', qty: 0, uom: '', remarks: '', position: '' });
  // Phase 78an — Masters (suppliers / items / uom) for the dialog.
  const [stopMasters, setStopMasters] = useState({ suppliers: [], items: [], uoms: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, i, u] = await Promise.all([
          api.get('/booking/suppliers').catch(() => ({ data: [] })),
          api.get('/booking/items').catch(() => ({ data: [] })),
          api.get('/booking/uom').catch(() => ({ data: [] })),
        ]);
        if (!cancelled) setStopMasters({ suppliers: s.data || [], items: i.data || [], uoms: u.data || [] });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const refreshDetail = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/trips/detail/${tripId}`);
      setData(d);
    } catch {}
  }, [tripId]);
  const openStopDialog = (side) => {
    setStopForm({ supplier_name: '', supplier_id: '', supplier_lat: 0, supplier_lng: 0, item: '', qty: 0, uom: '', remarks: '', position: '' });
    setStopDialog({ side });
  };
  const submitExtraStop = async () => {
    if (!stopForm.supplier_name.trim()) { alert('Supplier / location name is required'); return; }
    try {
      const payload = {
        side: stopDialog.side,
        supplier_name: stopForm.supplier_name.trim(),
        location: stopForm.supplier_name.trim(),
        item: stopForm.item.trim(),
        qty: Number(stopForm.qty) || 0,
        uom: stopForm.uom.trim(),
        remarks: stopForm.remarks.trim(),
      };
      // Phase 78an — pass selected supplier's coords directly.
      if (stopForm.supplier_lat && stopForm.supplier_lng) {
        payload.lat = stopForm.supplier_lat;
        payload.lng = stopForm.supplier_lng;
      }
      if (stopForm.position) payload.position = Number(stopForm.position);
      await api.post(`/trips/${tripId}/extra-stop`, payload);
      setStopDialog(null);
      refreshDetail();
    } catch (err) {
      alert(err.response?.data?.detail || 'Add stop failed');
    }
  };
  const deleteExtraStop = async (stopId) => {
    if (!window.confirm('Delete this extra stop?')) return;
    try {
      await api.delete(`/trips/${tripId}/extra-stop/${stopId}`);
      refreshDetail();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Phase 78ai+aj — Google Routes optimisation with dry-run preview.
  const [previewModal, setPreviewModal] = useState(null); // { preview }
  const optimizeStops = async () => {
    try {
      const { data: r } = await api.post(`/trips/${tripId}/extra-stops/optimize`, { dry_run: true });
      const anyChange = r.pickup?.changed || r.drop?.changed;
      if (!anyChange) {
        const msg = [];
        for (const side of ['pickup', 'drop']) {
          const v = r[side];
          if (v?.reason) msg.push(`${side.toUpperCase()}: ${v.reason}`);
        }
        alert(msg.length ? msg.join('\n') : 'Nothing to optimise.');
        return;
      }
      setPreviewModal({ preview: r });
    } catch (err) {
      alert(err.response?.data?.detail || 'Optimisation failed');
    }
  };

  // Flat list of all photos on this trip. Re-built whenever the trip data
  // changes so arrow-key navigation is in sync with what's visible.
  const panelPhotos = useMemo(() => {
    if (!data?.trip) return [];
    const t = data.trip;
    const dcs = (t.dc_photos && t.dc_photos.length > 0)
      ? t.dc_photos.map((d, i) => ({
          url: typeof d === 'string' ? d : d.url,
          gps: typeof d === 'string' ? null : d.gps,
          label: `DC ${i + 1}` + (typeof d !== 'string' && d.added_by ? ` (${d.added_by})` : ''),
        }))
      : (t.dc_photo ? [{ url: t.dc_photo, gps: t.dc_photo_gps, label: 'DC' }] : []);
    return [
      { url: t.start_km_photo, gps: t.start_km_photo_gps, label: 'KM Start' },
      { url: t.goods_photo, gps: t.goods_photo_gps, label: 'Goods' },
      { url: t.unload_photo, gps: t.unload_photo_gps, label: 'Unloaded' },
      { url: t.receipt_photo, gps: t.receipt_photo_gps, label: 'Receipt' },
      ...dcs,
    ].filter(p => p.url);
  }, [data]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/trips/detail/${tripId}`);
        setData(d);
      } catch {} finally { setLoading(false); }
    })();
  }, [tripId]);

  // Request directions from Google Maps when we have pickup/drop coords but no GPS tracks
  useEffect(() => {
    if (!isLoaded || !data || directionsRequested) return;
    const { trip, gps_tracks, google_kms: gKms } = data;
    const hasGpsTracks = gps_tracks && gps_tracks.length > 5;
    const hasCoords = trip.pickup_lat && trip.pickup_lng && trip.drop_lat && trip.drop_lng;

    if (hasCoords && window.google) {
      // Phase 78az / 78ba — skip Routes API + DirectionsService when the
      // admin has turned off the `polyline_trip_monitoring_map` flag.
      if (!getGoogleApiFlag('polyline_trip_monitoring_map')) {
        return;
      }
      setDirectionsRequested(true);

      const tryComputeRoute = async () => {
        // Try new Routes API first
        try {
          const { Route } = await window.google.maps.importLibrary('routes');
          const request = {
            origin: { lat: trip.pickup_lat, lng: trip.pickup_lng },
            destination: { lat: trip.drop_lat, lng: trip.drop_lng },
            travelMode: 'DRIVE',
            fields: ['path', 'distanceMeters', 'duration']
          };
          const { routes } = await Route.computeRoutes(request);
          if (routes && routes.length > 0) {
            trackGoogleApiUsage('polyline_trip_monitoring_map', 1);
            const route = routes[0];
            if (route.path) {
              setDirections({ routesApiPath: route.path.map(p => ({ lat: p.lat(), lng: p.lng() })) });
            }
            if (route.distanceMeters && !gKms) {
              const distKm = Math.round(route.distanceMeters / 100) / 10;
              setData(prev => ({ ...prev, google_kms: distKm }));
              api.put(`/trips/${trip.id}/update-google-kms`, { google_kms: distKm }).catch(() => {});
            }
            return;
          }
        } catch (e) {
          console.log('Routes API not available, trying DirectionsService...', e.message);
        }

        // Fallback: legacy DirectionsService
        try {
          const directionsService = new window.google.maps.DirectionsService();
          directionsService.route(
            {
              origin: { lat: trip.pickup_lat, lng: trip.pickup_lng },
              destination: { lat: trip.drop_lat, lng: trip.drop_lng },
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === 'OK') {
                trackGoogleApiUsage('polyline_trip_monitoring_map', 1);
                setDirections(result);
                const leg = result.routes?.[0]?.legs?.[0];
                if (leg?.distance?.value && !gKms) {
                  const distKm = Math.round(leg.distance.value / 100) / 10;
                  setData(prev => ({ ...prev, google_kms: distKm }));
                  api.put(`/trips/${trip.id}/update-google-kms`, { google_kms: distKm }).catch(() => {});
                }
              }
            }
          );
        } catch (e2) {
          console.log('DirectionsService also failed:', e2.message);
        }
      };
      tryComputeRoute();
    }
  }, [isLoaded, data, directionsRequested]);

  // Keyboard navigation in the photo lightbox: ← prev, → next, Esc close.
  useEffect(() => {
    if (zoomedIndex === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setZoomedIndex(null); return; }
      if (!panelPhotos.length) return;
      if (e.key === 'ArrowRight') {
        setZoomedIndex(i => (i + 1) % panelPhotos.length);
      } else if (e.key === 'ArrowLeft') {
        setZoomedIndex(i => (i - 1 + panelPhotos.length) % panelPhotos.length);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomedIndex, panelPhotos]);

  // Route Replay: auto-advance the slider when playing
  useEffect(() => {
    if (!isPlaying) return;
    const total = data?.gps_tracks?.length || 0;
    if (total < 2) return;
    // Tick every 500ms; advance N points based on speed (1x = 1pt, 2x = 2pts, 4x = 4pts)
    const interval = setInterval(() => {
      setReplayIndex(prev => {
        const next = prev + playbackSpeed;
        if (next >= total - 1) {
          setIsPlaying(false);
          return total - 1;        }
        return next;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [isPlaying, playbackSpeed, data]);

  if (loading) return <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"><div className="text-white text-sm">Loading trip details...</div></div>;
  if (!data) return null;

  const { trip, gps_tracks, fuel_records, stops, manual_kms, google_kms, traveled_km } = data;
  const routePath = gps_tracks.map(p => ({ lat: p.lat, lng: p.lng }));
  const hasRoute = routePath.length > 1;
  const hasCoords = trip.pickup_lat && trip.pickup_lng && trip.drop_lat && trip.drop_lng;
  const center = hasRoute
    ? routePath[Math.floor(routePath.length / 2)]
    : hasCoords ? { lat: (trip.pickup_lat + trip.drop_lat) / 2, lng: (trip.pickup_lng + trip.drop_lng) / 2 }
    : { lat: 13.0827, lng: 80.2707 };

  // Speed readings along the route — show at ~30 second intervals wherever GPS recorded
  const speedMarkers = [];
  if (gps_tracks.length > 0) {
    let lastTime = null;
    for (let i = 0; i < gps_tracks.length; i++) {
      const pt = gps_tracks[i];
      const speed = Math.round(pt.speed || 0);
      const ptTime = new Date(pt.timestamp).getTime();
      // Show marker every ~30 seconds, or if speed is 0 (stopped)
      if (!lastTime || (ptTime - lastTime) >= 30000) {
        speedMarkers.push({ lat: pt.lat, lng: pt.lng, speed, time: pt.timestamp });
        lastTime = ptTime;
      }
    }
  }
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Route Replay: only available for completed trips with sufficient GPS data
  const canReplay = trip.status === 'completed' && gps_tracks.length > 1;
  const safeReplayIdx = Math.min(replayIndex, Math.max(0, gps_tracks.length - 1));
  const replayPoint = canReplay ? gps_tracks[safeReplayIdx] : null;
  const replayPath = canReplay ? routePath.slice(0, safeReplayIdx + 1) : [];
  const replaySpeed = replayPoint ? Math.round(replayPoint.speed || 0) : 0;
  const replayTime = replayPoint?.timestamp;
  // Progress percent for visual feedback
  const replayProgress = canReplay ? Math.round((safeReplayIdx / (gps_tracks.length - 1)) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex" data-testid="trip-detail-panel">
      {/* Close button */}
      <button onClick={onClose} className="absolute top-4 right-4 z-[60] bg-white p-2 hover:bg-[#F3F3F3]" data-testid="close-trip-detail"><X size={20} /></button>

      <div className="flex w-full h-full">
        {/* LEFT SIDE — Trip Details */}
        <div className="w-[420px] bg-white overflow-y-auto border-r border-[#E5E5E5] flex-shrink-0">
          <div className="p-6 space-y-5">
            {/* Header */}
            <div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-[#666]">#{trip.id?.slice(0, 8)}</span>
                <div className="flex items-center gap-2">
                  {trip.is_pooled_into && (
                    <span className="text-xs uppercase tracking-widest px-2 py-1 bg-[#A855F7]/10 text-[#A855F7] font-bold" data-testid="pooled-badge">
                      Pooled with #{String(trip.is_pooled_into).slice(0, 8)}
                    </span>
                  )}
                  {Array.isArray(trip.pooled_items) && trip.pooled_items.length > 0 && (
                    <span className="text-xs uppercase tracking-widest px-2 py-1 bg-[#A855F7]/10 text-[#A855F7] font-bold" data-testid="pool-lead-badge">
                      Pool lead +{trip.pooled_items.length}
                    </span>
                  )}
                  {trip.status === 'pending' && trip.queue_position && (
                    <span className="text-xs uppercase tracking-widest px-2 py-1 bg-[#FF8C00]/10 text-[#FF8C00] font-bold">
                      Queue #{trip.queue_position}
                    </span>
                  )}
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${STATUS_BADGE[trip.status] || ''}`}>{trip.status}</span>
                </div>
              </div>
              <h2 className="text-xl font-bold tracking-tight mt-1">{trip.pickup_supplier_name} → {trip.drop_supplier_name}</h2>

              {/* Capacity warning — Phase 76e. Surfaces over-pooled trips so
                  admin sees them immediately and can split the pool or
                  change the pilot/vehicle. */}
              {trip.capacity_exceeded && (
                <div
                  className="mt-3 bg-[#FF3333]/10 border border-[#FF3333]/30 text-[#FF3333] p-2 rounded text-xs flex items-start gap-2"
                  data-testid="capacity-warning-banner"
                >
                  <span className="font-bold mt-0.5">⚠ CAPACITY EXCEEDED</span>
                  <span className="flex-1">{trip.capacity_warning || 'Total quantity exceeds the configured vehicle capacity for this item/UOM.'}</span>
                </div>
              )}

              {/* Admin actions — Phase 76d / 76l / 76v. Pure routing:
                  - Edit Trip ⇒ available on EVERY non-cancelled trip
                    (pending, allocated, in_progress, pickup_done,
                    drop_reached, completed, pooled stubs, pool leads).
                    Backend `PUT /trips/{id}/edit` allows the same.
                  - Re-run Auto / Pick Pilot ⇒ only meaningful while the
                    trip is still in the active flow (not completed).
                  - Detach From Pool ⇒ only on pooled stubs that are still
                    actively pooled. */}
              {currentUser?.role === 'admin' && trip.status !== 'cancelled' && (
                <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="admin-trip-actions">
                  {trip.status !== 'completed' && (trip.is_pooled_into ? (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm('Detach this booking from the pool? It will become its own pending trip and the auto-allocator will pick a pilot for it independently.')) return;
                        try {
                          await api.put(`/trips/${trip.id}/detach-from-pool`);
                          const { data: d } = await api.get(`/trips/detail/${trip.id}`);
                          setData(d);
                        } catch (err) {
                          alert(err.response?.data?.detail || 'Detach failed');
                        }
                      }}
                      className="text-[10px] uppercase tracking-widest font-bold bg-[#A855F7]/10 text-[#A855F7] hover:bg-[#A855F7] hover:text-white px-3 py-1.5 rounded-full transition-colors"
                      data-testid="detach-from-pool-btn"
                    >
                      ⊟ Detach From Pool
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Re-run auto allocation? The current pilot (if any) will be unassigned and the engine will pick the best match.')) return;
                          try {
                            await api.put(`/trips/${trip.id}/cancel`).catch(() => {});
                            // We use push-auto which puts trip back to pending and runs the engine.
                            await api.put(`/trips/${trip.id}/push-auto`);
                            const { data: d } = await api.get(`/trips/detail/${trip.id}`);
                            setData(d);
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Re-allocate failed');
                          }
                        }}
                        className="text-[10px] uppercase tracking-widest font-bold bg-[#00C853]/10 text-[#00C853] hover:bg-[#00C853] hover:text-white px-3 py-1.5 rounded-full transition-colors"
                        data-testid="reallocate-auto-btn"
                      >
                        ↻ Re-run Auto
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const { data: pilots } = await api.get(`/trips/${trip.id}/available-pilots`);
                            if (!pilots || pilots.length === 0) {
                              alert('No available pilots right now.');
                              return;
                            }
                            const pid = window.prompt(
                              'Type the number of the pilot to assign:\n\n' +
                              pilots.map((p, i) => `${i + 1}. ${p.name} (${p.vehicle_number || 'no veh'}) · ${p.distance_km}km · ${p.active_trips} active`).join('\n')
                            );
                            const idx = parseInt(pid) - 1;
                            if (isNaN(idx) || idx < 0 || idx >= pilots.length) return;
                            const chosen = pilots[idx];
                            await api.put(`/trips/${trip.id}/allocate`, { pilot_id: chosen.id });
                            const { data: d } = await api.get(`/trips/detail/${trip.id}`);
                            setData(d);
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Allocation failed');
                          }
                        }}
                        className="text-[10px] uppercase tracking-widest font-bold bg-[#FF8C00]/10 text-[#FF8C00] hover:bg-[#FF8C00] hover:text-white px-3 py-1.5 rounded-full transition-colors"
                        data-testid="manual-pick-pilot-btn"
                      >
                        ✎ Pick Pilot Manually
                      </button>
                    </>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    className="text-[10px] uppercase tracking-widest font-bold bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-3 py-1.5 rounded-full transition-colors"
                    data-testid="edit-trip-btn"
                  >
                    ✎ Edit Trip
                  </button>
                  {/* Phase 77c — Admin force-close a live trip when pilot
                      forgot to tap Drop Done. Only meaningful while the
                      trip is on the road (not pending / completed / cancelled). */}
                  {['in_progress', 'pickup_reached', 'pickup_done', 'drop_reached'].includes(trip.status) && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`Force-close this trip on the pilot's behalf?\n\nUse this only when the pilot forgot to tap "Drop Done". The trip will be marked completed now, and the standard completion WhatsApp + summary will fire.`)) return;
                        try {
                          await api.put(`/trips/${trip.id}/admin-close`);
                          const { data: d } = await api.get(`/trips/detail/${trip.id}`);
                          setData(d);
                        } catch (err) {
                          alert(err.response?.data?.detail || 'Close failed');
                        }
                      }}
                      className="text-[10px] uppercase tracking-widest font-bold bg-[#FF3333]/10 text-[#FF3333] hover:bg-[#FF3333] hover:text-white px-3 py-1.5 rounded-full transition-colors"
                      data-testid="admin-close-trip-btn"
                    >
                      ✕ Close Trip
                    </button>
                  )}
                </div>
              )}

              {/* Phase 78aa — Pooled children rendered with the same
                  italic + underline + smaller-font + lavender-card style
                  the pilot app uses (Phase 78z). Lead trip text stays
                  bold (already handled by the parent block above) so
                  admin can tell the lead from the children at a glance. */}
              {Array.isArray(trip.pooled_items) && trip.pooled_items.length > 0 && (
                <div
                  className="mt-3 border-l-4 border-[#A855F7] bg-[#F3E8FF] pl-2.5 py-1.5 space-y-2 rounded-r-2xl"
                  data-testid="pooled-items-list"
                >
                  <p className="text-[9px] uppercase tracking-widest text-[#7C3AED] font-bold">
                    +{trip.pooled_items.length} Pooled bookings (italic = pooled)
                  </p>
                  {trip.pooled_items.map((p, i) => (
                    <div
                      key={p.id || p.booking_id || i}
                      className="rounded-lg bg-white border border-[#A855F7]/30 px-2 py-1.5 text-[9px] italic underline decoration-[#A855F7]/40 underline-offset-2 space-y-1 text-[#5B21B6]"
                      data-testid={`admin-pooled-item-${trip.id}-${i}`}
                    >
                      <div className="flex items-center justify-between gap-1 not-italic no-underline">
                        <span className="font-mono text-[#A855F7] font-bold">
                          #{p.booking_id || p.id?.slice(0, 8) || `pool-${i + 1}`}
                        </span>
                        {p.priority && (
                          <span className="text-[9px] rounded-full px-1.5 py-0.5 bg-[#808080]/10 text-[#666]">{p.priority}</span>
                        )}
                      </div>
                      {(p.pickup_supplier_name || p.drop_supplier_name) && (
                        <div className="flex items-start gap-1.5">
                          <div className="flex flex-col items-center pt-0.5 not-italic no-underline">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#00C853]" />
                            <div className="w-px h-3 bg-[#E5E5E5]" />
                            <div className="w-1.5 h-1.5 rounded-full bg-[#FF3333]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate">{p.pickup_supplier_name || '—'}</p>
                            <p className="truncate mt-0.5">{p.drop_supplier_name || '—'}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                        {p.item && <span>{p.item} · {p.qty ?? 0} {p.uom || ''}</span>}
                        {p.booked_by_name && (
                          <span>
                            By {p.booked_by_name}
                            {p.booked_by_mobile && (
                              <a
                                href={`tel:${p.booked_by_mobile}`}
                                className="ml-1 text-[#002FA7] not-italic no-underline font-mono"
                                onClick={(e) => e.stopPropagation()}
                              >
                                ☎ {p.booked_by_mobile}
                              </a>
                            )}
                          </span>
                        )}
                        {p.booked_from_halt && <span>From {p.booked_from_halt}</span>}
                      </div>
                      {p.remarks && (
                        <p className="text-[9px] not-italic no-underline text-[#B27300] bg-[#FFF8E6] border border-[#FFD27F]/40 rounded px-1.5 py-1 whitespace-pre-wrap">
                          <span className="font-bold mr-1">REMARK</span>{p.remarks}
                        </p>
                      )}
                      {/* Phase 78aa — Per-pooled-child progress timestamps,
                          shown only when the child's pickup or drop location
                          differs from the lead trip and the pilot has marked
                          a step (writes happen via the pilot app). */}
                      {(p.pickup_reached_at || p.pickup_done_at || p.drop_reached_at || p.drop_done_at) && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 not-italic no-underline text-[9px] text-[#0A0A0A]">
                          {p.pickup_reached_at && <span>P-Reach <span className="font-mono">{new Date(p.pickup_reached_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span></span>}
                          {p.pickup_done_at && <span>P-Done <span className="font-mono">{new Date(p.pickup_done_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span></span>}
                          {p.drop_reached_at && <span>D-Reach <span className="font-mono">{new Date(p.drop_reached_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span></span>}
                          {p.drop_done_at && <span>D-Done <span className="font-mono">{new Date(p.drop_done_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span></span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Phase 78ag+ah — Additional Stops with drag-to-reorder.
                  Admin can insert at any position via dialog OR drag
                  rows to re-sequence. Stops reached by pilot are pinned. */}
              {(() => {
                const ep = trip.extra_pickups || [];
                const ed = trip.extra_drops || [];
                const canAdd = currentUser?.role === 'admin' && [
                  'awaiting_goods_ready', 'pending', 'allocated', 'accepted',
                  'in_progress', 'pickup_reached', 'pickup_done', 'drop_reached', 'pooled',
                ].includes(trip.status);
                if (!canAdd && ep.length === 0 && ed.length === 0) return null;
                return (
                  <div className="mt-3 rounded-xl border border-[#A855F7]/30 bg-[#F3E8FF] p-2 space-y-2" data-testid={`admin-trip-extras-${trip.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] uppercase tracking-widest text-[#7C3AED] font-bold">
                        Additional Stops · drag to re-sequence
                      </p>
                      {canAdd && (ep.length >= 2 || ed.length >= 2) && (
                        <button
                          type="button"
                          onClick={optimizeStops}
                          className="text-[10px] uppercase tracking-widest font-bold bg-white text-[#7C3AED] border border-[#7C3AED]/40 rounded-full px-2.5 py-1 hover:bg-[#7C3AED] hover:text-white transition-colors"
                          data-testid={`admin-optimize-stops-${trip.id}`}
                          title="Use Google Maps to suggest the shortest order"
                        >🧭 Optimise Order</button>
                      )}
                    </div>
                    <SortableExtraStops
                      tripId={trip.id}
                      side="pickup"
                      stops={ep}
                      canEdit={canAdd}
                      labelPrefix="PICKUP STOP"
                      colorHex="#00C853"
                      onChange={refreshDetail}
                      onDelete={deleteExtraStop}
                    />
                    <SortableExtraStops
                      tripId={trip.id}
                      side="drop"
                      stops={ed}
                      canEdit={canAdd}
                      labelPrefix="DROP STOP"
                      colorHex="#FF3333"
                      onChange={refreshDetail}
                      onDelete={deleteExtraStop}
                    />
                    {canAdd && (
                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => openStopDialog('pickup')}
                          className="flex-1 text-[10px] uppercase tracking-widest text-[#00C853] border border-[#00C853]/30 rounded-full py-1.5 hover:bg-[#00C853]/5 disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={ep.length >= 3}
                          title={ep.length >= 3 ? 'Max 4 pickup points reached' : 'Add an extra pickup point'}
                          data-testid={`admin-add-pickup-stop-${trip.id}`}
                        >+ Add Pickup Stop ({1 + ep.length}/4)</button>
                        <button
                          type="button"
                          onClick={() => openStopDialog('drop')}
                          className="flex-1 text-[10px] uppercase tracking-widest text-[#FF3333] border border-[#FF3333]/30 rounded-full py-1.5 hover:bg-[#FF3333]/5 disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={ed.length >= 3}
                          title={ed.length >= 3 ? 'Max 4 drop points reached' : 'Add an extra drop point'}
                          data-testid={`admin-add-drop-stop-${trip.id}`}
                        >+ Add Drop Stop ({1 + ed.length}/4)</button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Phase 76m — Pool decision history accordion. Visible for any
                  trip; when no offers/pool relations exist it stays
                  collapsed and shows "no pool activity" inline. */}
              <PoolHistoryAccordion tripId={trip.id} />

              {/* Phase 76w — Edit history accordion. Visible whenever the
                  trip has any edit_log entries. */}
              {Array.isArray(trip.edit_log) && trip.edit_log.length > 0 && (
                <EditHistoryAccordion entries={trip.edit_log} />
              )}
            </div>

            {/* Booking Info */}
            <div className="border-l-4 border-[#002FA7] pl-3 space-y-1">
              <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Booking Info</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-[#666]">Booked By</span>
                <span className="font-medium flex items-center gap-2 flex-wrap">
                  <span>{trip.booked_by_name || '-'}</span>
                  {trip.booked_by_mobile && (
                    <a
                      href={`tel:${trip.booked_by_mobile}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] font-mono text-[#002FA7] bg-[#002FA7]/10 px-1.5 py-0.5 rounded hover:bg-[#002FA7]/20 whitespace-nowrap"
                      data-testid={`detail-booker-mobile-${trip.id}`}
                    >
                      {trip.booked_by_mobile}
                    </a>
                  )}
                </span>
                <span className="text-[#666]">Pilot</span><span className="font-medium inline-flex items-center gap-1">{trip.pilot_name || '-'}<ManualBadge mode={trip.pilot_allocation_mode} /></span>
                <span className="text-[#666]">Vehicle</span><span className="font-medium">{trip.vehicle_number || '-'}</span>
                <span className="text-[#666]">Item</span><span className="font-medium">{trip.item || '-'}</span>
                <span className="text-[#666]">Qty / UOM</span><span className="font-medium">{trip.qty || 0} {trip.uom || ''}</span>
                <span className="text-[#666]">Priority</span><span className={`font-medium ${trip.priority === 'shortage' ? 'text-[#FF3333]' : ''}`}>{trip.priority || 'others'}</span>
                {trip.preferred_pilot_name && <><span className="text-[#666]">Preferred Pilot</span><span className="font-medium">{trip.preferred_pilot_name}</span></>}
                <span className="text-[#666]">Allocation</span>
                <span className="font-medium">
                  {(() => {
                    const mode = trip.allocation_mode || (trip.allocation_type === 'manual' || trip.preferred_pilot_name ? 'manual' : 'auto');
                    const isManual = mode === 'manual';
                    return (
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 ${isManual ? 'bg-[#FF8C00]/10 text-[#FF8C00]' : 'bg-[#00C853]/10 text-[#00C853]'}`}>
                        {isManual ? 'Manual' : 'Auto'}
                      </span>
                    );
                  })()}
                  {trip.manual_allocated_by && <span className="text-[10px] text-[#666] ml-2">by {trip.manual_allocated_by}</span>}
                </span>
              </div>
            </div>

            {/* Pickup / Drop GPS section — admin can pin missing supplier coords from here */}
            <div className="border-l-4 border-[#FF8C00] pl-3 space-y-1">
              <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Locations</p>
              {[
                { side: 'pickup', name: trip.pickup_supplier_name, lat: trip.pickup_lat, lng: trip.pickup_lng, color: '#00C853', label: 'Pickup' },
                { side: 'drop', name: trip.drop_supplier_name, lat: trip.drop_lat, lng: trip.drop_lng, color: '#FF3333', label: 'Drop' },
              ].map(loc => {
                const hasGps = !!(loc.lat && loc.lng);
                return (
                  <div key={loc.side} className="flex items-center justify-between gap-2 text-sm py-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: loc.color }} />
                      <span className="text-[#666] text-xs uppercase tracking-widest w-12 flex-shrink-0">{loc.label}</span>
                      <span className="font-medium truncate">{loc.name || '-'}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {hasGps ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#00C853] bg-[#00C853]/10 px-1.5 py-0.5 rounded" data-testid={`tm-${loc.side}-gps-ok-${trip.id}`}>✓ GPS</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#FF3333] bg-[#FF3333]/10 px-1.5 py-0.5 rounded animate-pulse" data-testid={`tm-${loc.side}-gps-missing-${trip.id}`}>● No GPS</span>
                      )}
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            // Resolve the supplier doc by name so the picker has full context (id, audit fields).
                            const { data: list } = await api.get('/booking/suppliers');
                            const sup = list.find(s => s.name === loc.name);
                            if (!sup) return alert('Supplier not found in master');
                            setLocDialog({ side: loc.side, supplier: sup });
                          } catch { alert('Failed to load supplier'); }
                        }}
                        className="text-[10px] font-bold uppercase tracking-widest text-[#002FA7] hover:underline flex items-center gap-1"
                        data-testid={`tm-${loc.side}-set-loc-${trip.id}`}
                      >
                        <MapPin size={11} weight="fill" /> {hasGps ? 'Edit' : 'Set'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Timeline */}
            <div className="border-l-4 border-[#00C853] pl-3 space-y-1">
              <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Timeline · Pilot Click vs Google GPS</p>
              <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                <span className="text-[#999] uppercase tracking-widest font-bold col-span-1"></span>
                <span className="text-[#999] uppercase tracking-widest font-bold">Pilot</span>
                <span className="text-[#002FA7] uppercase tracking-widest font-bold">Google GPS</span>

                <span className="text-[#666]">Created</span>
                <span className="col-span-2">{fmtTime(trip.created_at)}</span>

                <span className="text-[#666]">Allocated</span>
                <span className="col-span-2">{fmtTime(trip.allocated_at)}</span>

                <span className="text-[#666]">Started</span>
                <span>{fmtTime(trip.started_at)}</span>
                <span data-testid={`trip-${trip.id}-google-start`} className="text-[#002FA7]">{fmtTime(trip.google_start_time)}</span>

                <span className="text-[#666]">Pickup Reached</span>
                <span>{fmtTime(trip.pickup_reached_at)}</span>
                <span data-testid={`trip-${trip.id}-google-pickup-reach`} className="text-[#002FA7]">{fmtTime(trip.google_pickup_reach_time)}</span>

                <span className="text-[#666]">Pickup Done</span>
                <span>{fmtTime(trip.pickup_done_at)}</span>
                <span data-testid={`trip-${trip.id}-google-pickup-complete`} className="text-[#002FA7]">{fmtTime(trip.google_pickup_complete_time)}</span>

                <span className="text-[#666]">Drop Reached</span>
                <span>{fmtTime(trip.drop_reached_at)}</span>
                <span data-testid={`trip-${trip.id}-google-drop-reach`} className="text-[#002FA7]">{fmtTime(trip.google_drop_reach_time)}</span>

                <span className="text-[#666]">Completed</span>
                <span>{fmtTime(trip.completed_at)}</span>
                <span data-testid={`trip-${trip.id}-google-drop-complete`} className="text-[#002FA7]">{fmtTime(trip.google_drop_complete_time)}</span>
              </div>
            </div>

            {/* KMs & Duration */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#F7F7F7] p-3 text-center">
                <Path size={18} className="mx-auto text-[#002FA7] mb-1" />
                <p className="text-lg font-black">{google_kms || '-'}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#666]">Google KMs</p>
              </div>
              <div className="bg-[#F7F7F7] p-3 text-center">
                <Path size={18} className="mx-auto text-[#00C853] mb-1" />
                <p className="text-lg font-black">{manual_kms || '-'}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#666]">Manual KMs</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#F7F7F7] p-3 text-center">
                <Clock size={18} className="mx-auto text-[#FF8C00] mb-1" />
                <p className="text-lg font-black">{fmtDuration(trip.started_at, trip.completed_at)}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#666]">Duration</p>
              </div>
              <div className="bg-[#F7F7F7] p-3 text-center">
                <Flag size={18} className="mx-auto text-[#FF3333] mb-1" />
                <p className="text-lg font-black">{stops.length}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#666]">Stops</p>
              </div>
            </div>

            {/* Waiting Times */}
            <div className="border-l-4 border-[#FFD700] pl-3 space-y-1">
              <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Waiting Times · Pilot vs Google GPS</p>
              <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                <span className="text-[#999] uppercase tracking-widest font-bold col-span-1"></span>
                <span className="text-[#999] uppercase tracking-widest font-bold">Pilot</span>
                <span className="text-[#002FA7] uppercase tracking-widest font-bold">Google GPS</span>

                <span className="text-[#666]">Pickup Wait</span>
                <span className={`font-medium ${(trip.pickup_waiting_minutes || 0) > 20 ? 'text-[#FF3333]' : ''}`}>{trip.pickup_waiting_minutes ? `${trip.pickup_waiting_minutes} min` : '-'}</span>
                <span data-testid={`trip-${trip.id}-google-pickup-wait`} className={`font-medium ${(trip.google_pickup_waiting_min || 0) > 20 ? 'text-[#FF3333]' : 'text-[#002FA7]'}`}>{trip.google_pickup_waiting_min != null ? `${trip.google_pickup_waiting_min} min` : '-'}</span>

                <span className="text-[#666]">Drop Wait</span>
                <span className={`font-medium ${(trip.drop_waiting_minutes || 0) > 20 ? 'text-[#FF3333]' : ''}`}>{trip.drop_waiting_minutes ? `${trip.drop_waiting_minutes} min` : '-'}</span>
                <span data-testid={`trip-${trip.id}-google-drop-wait`} className={`font-medium ${(trip.google_drop_waiting_min || 0) > 20 ? 'text-[#FF3333]' : 'text-[#002FA7]'}`}>{trip.google_drop_waiting_min != null ? `${trip.google_drop_waiting_min} min` : '-'}</span>

                <span className="text-[#666]">Total</span>
                <span className="font-medium">{fmtDuration(trip.started_at, trip.completed_at)}</span>
                <span data-testid={`trip-${trip.id}-google-total`} className="font-medium text-[#002FA7]">{trip.google_total_min != null ? `${Math.round(trip.google_total_min)} min` : '-'}</span>
              </div>
            </div>

            {/* Helpers */}
            {Array.isArray(trip.loadmen) && trip.loadmen.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest font-bold text-[#666]">
                  Helpers ({trip.loadmen.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {trip.loadmen.map(l => (
                    <span
                      key={l.loadman_id}
                      className={`text-xs px-2 py-1 ${l.type === 'contract' ? 'bg-[#FF8C00]/10 text-[#FF8C00]' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}
                      title={`${l.type === 'contract' ? 'Contract' : 'Own'} · ${l.halt_location || '—'}`}
                    >
                      {l.name}{l.emp_id ? ` (${l.emp_id})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* KM Readings — always render (Phase 76b). Travelled KMs from
                gps_tracks works even when supplier master has no GPS pins. */}
            <div className="border-l-4 border-[#808080] pl-3 space-y-1">
              <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Odometer + GPS Distance</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-[#666]">Start KM</span><span className="font-medium">{trip.start_km || '-'}</span>
                <span className="text-[#666]">End KM</span><span className="font-medium">{trip.end_km || '-'}</span>
                <span className="text-[#666]">Manual KMs</span><span className="font-medium font-bold text-[#00C853]">{manual_kms || '-'}</span>
                <span className="text-[#666]">Google KMs</span><span className="font-medium font-bold text-[#002FA7]">{google_kms || '-'}</span>
                <span className="text-[#666]" title="Sum of GPS-track distances (haversine)">Travelled (GPS)</span>
                <span className="font-medium font-bold text-[#FF3333]" data-testid="trip-traveled-km">{traveled_km ? `${traveled_km} km` : '-'}</span>
              </div>
              {manual_kms > 0 && google_kms > 0 && (
                <div className="mt-2 p-2 bg-[#F7F7F7] text-xs text-[#666]">
                  Variance: <span className={`font-bold ${Math.abs(manual_kms - google_kms) > google_kms * 0.2 ? 'text-[#FF3333]' : 'text-[#00C853]'}`}>
                    {manual_kms > google_kms ? '+' : ''}{(manual_kms - google_kms).toFixed(1)} KMs
                  </span>
                  {Math.abs(manual_kms - google_kms) > google_kms * 0.2 && <span className="ml-2 text-[#FF3333]">(High variance!)</span>}
                </div>
              )}
            </div>

            {/* Photos */}
            {panelPhotos.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Photos ({panelPhotos.length})</p>
                <div className="grid grid-cols-2 gap-2">
                  {panelPhotos.map((p, idx) => {
                    const hasGps = p.gps && (p.gps.lat || p.gps.lat === 0) && (p.gps.lng || p.gps.lng === 0);
                    const mapsHref = hasGps ? `https://www.google.com/maps/search/?api=1&query=${p.gps.lat},${p.gps.lng}` : null;
                    const fullUrl = p.url.startsWith('/') ? `${API_BASE}${p.url}` : p.url;
                    return (
                      <div
                        key={`${p.label}-${idx}`}
                        className="border border-[#E5E5E5] relative group cursor-zoom-in"
                        data-testid={`trip-photo-${p.label.toLowerCase().replace(/[\s()]/g, '-')}`}
                        onClick={() => setZoomedIndex(idx)}
                      >
                        <img src={fullUrl} alt={p.label} className="w-full h-24 object-cover transition-opacity group-hover:opacity-80" />
                        {hasGps && (
                          <a
                            href={mapsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            title={`GPS: ${p.gps.lat.toFixed(6)}, ${p.gps.lng.toFixed(6)}${p.gps.accuracy ? ` (±${Math.round(p.gps.accuracy)}m)` : ''} — tap to open in Maps`}
                            className="absolute top-1 right-1 bg-[#00C853] text-white rounded-full p-1 shadow-md hover:bg-[#00C853]/90"
                            data-testid={`trip-photo-gps-${p.label.toLowerCase().replace(/[\s()]/g, '-')}`}
                          >
                            <MapPin size={12} weight="fill" />
                          </a>
                        )}
                        <p className="text-[10px] text-center text-[#666] py-1 uppercase tracking-widest">
                          {p.label}
                          {hasGps && <span className="ml-1 text-[#00C853]">· GPS</span>}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fuel Records */}
            {fuel_records.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest font-bold text-[#666]"><GasPump size={14} className="inline mr-1" />Fuel Stops</p>
                {fuel_records.map(f => (
                  <div key={f.id} className="bg-[#F7F7F7] p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-[#666]">KM</span><span className="font-medium">{f.km_reading}</span></div>
                    <div className="flex justify-between"><span className="text-[#666]">Fuel</span><span className="font-medium">{f.fuel_amount} L</span></div>
                    <div className="flex justify-between"><span className="text-[#666]">Cost</span><span className="font-medium">{f.fuel_cost}</span></div>
                    <div className="flex justify-between"><span className="text-[#666]">Status</span><span className={`text-xs uppercase ${f.approval_status === 'approved' ? 'text-[#00C853]' : 'text-[#FF8C00]'}`}>{f.approval_status}</span></div>
                  </div>
                ))}
              </div>
            )}

            {/* Stops */}
            {stops.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest font-bold text-[#666]">Stops Along Route</p>
                {stops.map((s, i) => (
                  <div key={i} className="bg-[#FF8C00]/5 border border-[#FF8C00]/20 p-2 text-xs flex justify-between">
                    <span>{fmtTime(s.start_time)}</span>
                    <span className="font-medium">{s.duration_minutes} min</span>
                    <span className="text-[#666]">{s.lat.toFixed(4)}, {s.lng.toFixed(4)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE — Google Map */}
        <div className="flex-1 bg-[#F3F3F3]">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={hasRoute ? 12 : hasCoords ? 11 : 14}
              options={{ styles: MAP_STYLES, disableDefaultUI: false, zoomControl: true, mapTypeControl: false, mapId: GOOGLE_MAPS_MAP_ID }}
            >
              {/* Planned road route via new Routes API (replaces deprecated DirectionsService).
                  Pre-cached path from /trips/detail (routesApiPath) wins; otherwise live fetch. */}
              {directions?.routesApiPath && (
                <Polyline path={directions.routesApiPath} options={{ strokeColor: '#002FA7', strokeWeight: 6, strokeOpacity: 0.85 }} />
              )}
              {!directions?.routesApiPath && hasCoords && (
                <RoutePolyline
                  origin={{ lat: trip.pickup_lat, lng: trip.pickup_lng }}
                  destination={{ lat: trip.drop_lat, lng: trip.drop_lng }}
                  usageKey="polyline_trip_monitoring_map"
                  options={{ strokeColor: '#002FA7', strokeWeight: 6, strokeOpacity: 0.85 }}
                />
              )}

              {/* Actual travelled route from GPS tracks (on top of planned route) */}
              {hasRoute && (
                <Polyline
                  path={routePath}
                  options={{
                    strokeColor: '#FF3333',
                    strokeWeight: 4,
                    strokeOpacity: 0.95,
                    zIndex: 2,
                  }}
                />
              )}

              {/* Start marker */}
              {(hasRoute || hasCoords) && (
                <AdvancedMarker
                  position={hasRoute ? routePath[0] : { lat: trip.pickup_lat, lng: trip.pickup_lng }}
                  icon={{ color: '#00C853', label: 'S', size: 30 }}
                  title={`Start: ${trip.pickup_supplier_name || ''} | ${fmtTime(trip.started_at)}`}
                />
              )}

              {/* End marker */}
              {(hasRoute || hasCoords) && (
                <AdvancedMarker
                  position={hasRoute ? routePath[routePath.length - 1] : { lat: trip.drop_lat, lng: trip.drop_lng }}
                  icon={{ color: '#FF3333', label: 'E', size: 30 }}
                  title={`End: ${trip.drop_supplier_name || ''} | ${fmtTime(trip.completed_at)}`}
                />
              )}

              {/* Speed readings — orange circles with km/h */}
              {speedMarkers.map((sm, i) => (
                <AdvancedMarker
                  key={`spd-${i}`}
                  position={{ lat: sm.lat, lng: sm.lng }}
                  icon={{ color: '#FF8C00', label: `${sm.speed}`, size: 22 }}
                  title={`${sm.speed} km/h at ${fmtTime(sm.time)}`}
                />
              ))}

              {/* Stop markers */}
              {stops.map((s, i) => (
                <AdvancedMarker
                  key={`stop-${i}`}
                  position={{ lat: s.lat, lng: s.lng }}
                  onClick={() => setSelectedStop(s)}
                  contentHTML={'<div style="width:14px;height:14px;background:#FF8C00;border:2px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,.3)"></div>'}
                  title={`Stop: ${s.duration_minutes} min`}
                />
              ))}

              {/* Route Replay — highlighted travelled path so far + moving marker */}
              {canReplay && replayPath.length > 1 && (
                <Polyline
                  path={replayPath}
                  options={{
                    strokeColor: '#002FA7',
                    strokeWeight: 6,
                    strokeOpacity: 0.95,
                    zIndex: 5,
                  }}
                />
              )}
              {canReplay && replayPoint && (
                <AdvancedMarker
                  position={{ lat: replayPoint.lat, lng: replayPoint.lng }}
                  contentHTML={'<div style="width:22px;height:22px;border-radius:50%;background:#002FA7;border:3px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4)"></div>'}
                  zIndex={999}
                  title={`Replay: ${replaySpeed} km/h at ${fmtTime(replayTime)}`}
                />
              )}

              {selectedStop && (
                <InfoWindow position={{ lat: selectedStop.lat, lng: selectedStop.lng }} onCloseClick={() => setSelectedStop(null)}>
                  <div className="p-1 text-xs">
                    <p className="font-bold">Stop: {selectedStop.duration_minutes} min</p>
                    <p>{fmtTime(selectedStop.start_time)} - {fmtTime(selectedStop.end_time)}</p>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : (
            <div className="h-full flex items-center justify-center text-[#666] text-sm">Loading map...</div>
          )}

          {/* Map Legend Overlay */}
          <div className="absolute bottom-4 left-[440px] bg-white/90 backdrop-blur border border-[#E5E5E5] p-3 text-xs space-y-1">
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#00C853]" />{trip.pickup_supplier_name || 'Start'} {trip.started_at ? `(${fmtTime(trip.started_at)})` : ''}</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#FF3333]" />{trip.drop_supplier_name || 'End'} {trip.completed_at ? `(${fmtTime(trip.completed_at)})` : ''}</div>
            {directions && <div className="flex items-center gap-2"><div className="w-8 h-1 bg-[#002FA7]" />Planned Route</div>}
            {hasRoute && <div className="flex items-center gap-2"><div className="w-8 h-1 bg-[#FF3333]" />Actual Travelled ({gps_tracks.length} pts)</div>}
            {google_kms > 0 && <div className="flex items-center gap-2"><Path size={14} className="text-[#002FA7]" />{google_kms} km (Google)</div>}
            {speedMarkers.length > 0 && <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#FF8C00]/70" />Speed (km/h)</div>}
            {stops.length > 0 && <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#FF8C00]" />Stops ({stops.length})</div>}
          </div>

          {/* Route Replay Slider — Admin-only, completed trips */}
          {canReplay && (
            <div
              className="absolute bottom-4 right-4 left-[680px] bg-white border border-[#E5E5E5] shadow-lg p-4"
              data-testid="route-replay-panel"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7]">Route Replay</span>
                  <span className="text-[10px] text-[#666]">{safeReplayIdx + 1} / {gps_tracks.length} pts · {replayProgress}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#666]">{fmtTime(replayTime)}</span>
                  <span className={`text-xs font-bold px-2 py-1 ${replaySpeed === 0 ? 'bg-[#FF3333]/10 text-[#FF3333]' : replaySpeed > 60 ? 'bg-[#FF8C00]/10 text-[#FF8C00]' : 'bg-[#00C853]/10 text-[#00C853]'}`}>
                    {replaySpeed} km/h
                  </span>
                </div>
              </div>

              <input
                type="range"
                min={0}
                max={Math.max(0, gps_tracks.length - 1)}
                value={safeReplayIdx}
                onChange={e => { setReplayIndex(parseInt(e.target.value, 10)); setIsPlaying(false); }}
                className="w-full h-2 accent-[#002FA7] cursor-pointer"
                data-testid="route-replay-slider"
              />

              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setReplayIndex(0); setIsPlaying(false); }}
                    className="p-2 border border-[#E5E5E5] hover:bg-[#F3F3F3]"
                    title="Restart"
                    data-testid="route-replay-restart-btn"
                  >
                    <ArrowCounterClockwise size={14} />
                  </button>
                  <button
                    onClick={() => {
                      // If we're at the end, restart on play
                      if (safeReplayIdx >= gps_tracks.length - 1) setReplayIndex(0);
                      setIsPlaying(p => !p);
                    }}
                    className="p-2 bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
                    title={isPlaying ? 'Pause' : 'Play'}
                    data-testid="route-replay-play-btn"
                  >
                    {isPlaying ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-[#666] mr-1">Speed</span>
                  {[1, 2, 4].map(s => (
                    <button
                      key={s}
                      onClick={() => setPlaybackSpeed(s)}
                      className={`text-xs font-bold px-2 py-1 ${playbackSpeed === s ? 'bg-[#002FA7] text-white' : 'bg-[#F3F3F3] text-[#666] hover:bg-[#E5E5E5]'}`}
                      data-testid={`route-replay-speed-${s}x`}
                    >
                      {s}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox — click any photo thumbnail to enlarge. ← / → keys navigate. */}
      {zoomedIndex !== null && panelPhotos[zoomedIndex] && (() => {
        const cur = panelPhotos[zoomedIndex];
        const fullUrl = cur.url.startsWith('/') ? `${API_BASE}${cur.url}` : cur.url;
        const hasGps = cur.gps && (cur.gps.lat || cur.gps.lat === 0) && (cur.gps.lng || cur.gps.lng === 0);
        const mapsHref = hasGps ? `https://www.google.com/maps/search/?api=1&query=${cur.gps.lat},${cur.gps.lng}` : null;
        const total = panelPhotos.length;
        const prev = () => setZoomedIndex((zoomedIndex - 1 + total) % total);
        const next = () => setZoomedIndex((zoomedIndex + 1) % total);
        return (
          <div
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setZoomedIndex(null)}
            data-testid="photo-lightbox"
          >
            <button
              onClick={() => setZoomedIndex(null)}
              className="absolute top-4 right-4 text-white hover:bg-white/10 p-2 rounded-full"
              title="Close (Esc)"
              data-testid="photo-lightbox-close"
            >
              <X size={24} />
            </button>

            {total > 1 && (
              <>
                <button
                  onClick={e => { e.stopPropagation(); prev(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 p-3 rounded-full"
                  title="Previous photo (←)"
                  data-testid="photo-lightbox-prev"
                >
                  <CaretLeft size={28} weight="bold" />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); next(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 p-3 rounded-full"
                  title="Next photo (→)"
                  data-testid="photo-lightbox-next"
                >
                  <CaretRight size={28} weight="bold" />
                </button>
              </>
            )}

            <div className="max-w-[95vw] max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
              <img
                src={fullUrl}
                alt={cur.label}
                className="max-w-full max-h-[80vh] object-contain"
                data-testid="photo-lightbox-img"
              />
              <div className="mt-3 flex items-center gap-3 text-white flex-wrap justify-center">
                <span className="text-sm uppercase tracking-widest font-bold" data-testid="photo-lightbox-label">{cur.label}</span>
                <span className="text-xs text-white/60 font-mono" data-testid="photo-lightbox-counter">{zoomedIndex + 1} / {total}</span>
                {mapsHref && (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs flex items-center gap-1 bg-[#00C853]/20 text-[#00C853] hover:bg-[#00C853]/30 px-2 py-1 rounded-full"
                    data-testid="photo-lightbox-gps-link"
                  >
                    <MapPin size={11} weight="fill" /> Open in Maps
                  </a>
                )}
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-white/60 hover:text-white underline"
                  data-testid="photo-lightbox-open-original"
                >
                  Open Original
                </a>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Supplier Location Picker (admin pinning a supplier from Trip Monitoring) */}
      {locDialog && (
        <SupplierLocationPicker
          open={!!locDialog}
          onClose={() => setLocDialog(null)}
          supplier={locDialog.supplier}
          currentUserId={currentUser?.id || ''}
          isAdmin={currentUser?.role === 'admin' || currentUser?.is_super_admin}
          onSaved={(updated) => {
            // Patch the local trip with the new coords so the map refreshes immediately.
            setData(prev => prev ? {
              ...prev,
              trip: {
                ...prev.trip,
                ...(locDialog.side === 'pickup' ? { pickup_lat: updated.lat, pickup_lng: updated.lng } : { drop_lat: updated.lat, drop_lng: updated.lng }),
              },
            } : prev);
          }}
        />
      )}
      {/* Phase 76l — Edit Trip dialog */}
      {editOpen && (
        <EditTripDialog
          trip={trip}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            setEditOpen(false);
            const { data: d } = await api.get(`/trips/detail/${trip.id}`);
            setData(d);
          }}
        />
      )}

      {/* Phase 78aj — Route preview modal (admin). */}
      {previewModal && (
        <RoutePreviewModal
          tripId={tripId}
          preview={previewModal.preview}
          onClose={() => setPreviewModal(null)}
          onApplied={() => { setPreviewModal(null); refreshDetail(); }}
        />
      )}

      {/* Phase 78ag — Admin Add/Insert Extra Stop dialog. Mirrors the
          booking-side dialog with an explicit position selector. */}
      {stopDialog && (() => {
        const sideField = stopDialog.side === 'pickup' ? 'extra_pickups' : 'extra_drops';
        const existingCount = (trip[sideField] || []).length;
        const slots = [];
        for (let p = 2; p <= existingCount + 2; p++) slots.push(p);
        return (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md p-5 space-y-3 rounded-2xl" data-testid="admin-extra-stop-dialog">
              <h2 className="text-lg font-bold">
                Add Extra {stopDialog.side === 'pickup' ? 'Pickup' : 'Drop'} Stop
              </h2>
              <p className="text-xs text-[#666]">Trip #{trip.id?.slice(0, 8)} · 1 main + {existingCount} extras already</p>
              {/* Phase 78an — Supplier picker from master. Pulls coords + address. */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#666] block mb-1">Supplier *</label>
                <SupplierSearchSelect
                  suppliers={stopMasters.suppliers}
                  value={stopForm.supplier_name}
                  onSelect={(sup) => {
                    if (!sup) {
                      setStopForm({ ...stopForm, supplier_name: '', supplier_id: '', supplier_lat: 0, supplier_lng: 0 });
                    } else {
                      setStopForm({
                        ...stopForm,
                        supplier_name: sup.name,
                        supplier_id: sup.id,
                        supplier_lat: sup.lat || 0,
                        supplier_lng: sup.lng || 0,
                      });
                    }
                  }}
                  placeholder="Search & select from master"
                  color={stopDialog.side === 'pickup' ? '#00C853' : '#FF3333'}
                  required
                  testIdPrefix="admin-extra-stop-supplier"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#666] block mb-1">
                  Insert at position (1 = main, 2-4 = extras)
                </label>
                <select
                  value={stopForm.position}
                  onChange={e => setStopForm({ ...stopForm, position: e.target.value })}
                  className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm bg-white"
                  data-testid="admin-extra-stop-position"
                >
                  <option value="">Append at end (position {existingCount + 2})</option>
                  {slots.map(p => (
                    <option key={p} value={p}>Position {p}{p === existingCount + 2 ? ' (end)' : ` (push current ${p} down)`}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {/* Phase 78an — Item & UOM autocompletes backed by master. */}
                <div className="col-span-1">
                  <label className="text-[9px] uppercase tracking-widest text-[#666] block mb-0.5">Item</label>
                  <input
                    type="text" list={`admin-extra-stop-item-list-${trip.id}`}
                    placeholder="Item"
                    value={stopForm.item}
                    onChange={e => setStopForm({ ...stopForm, item: e.target.value })}
                    className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm"
                    data-testid="admin-extra-stop-item"
                  />
                  <datalist id={`admin-extra-stop-item-list-${trip.id}`}>
                    {(stopMasters.items || []).map(i => <option key={i.id} value={i.name} />)}
                  </datalist>
                </div>
                <div className="col-span-1">
                  <label className="text-[9px] uppercase tracking-widest text-[#666] block mb-0.5">Qty</label>
                  <input
                    type="number" placeholder="0" value={stopForm.qty}
                    onChange={e => setStopForm({ ...stopForm, qty: e.target.value })}
                    className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm"
                    data-testid="admin-extra-stop-qty"
                  />
                </div>
                <div className="col-span-1">
                  <label className="text-[9px] uppercase tracking-widest text-[#666] block mb-0.5">UOM</label>
                  <input
                    type="text" list={`admin-extra-stop-uom-list-${trip.id}`}
                    placeholder="UOM"
                    value={stopForm.uom}
                    onChange={e => setStopForm({ ...stopForm, uom: e.target.value })}
                    className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm"
                    data-testid="admin-extra-stop-uom"
                  />
                  <datalist id={`admin-extra-stop-uom-list-${trip.id}`}>
                    {(stopMasters.uoms || []).map(u => <option key={u.id} value={u.name} />)}
                  </datalist>
                </div>
              </div>
              <textarea placeholder="Remarks (optional)" value={stopForm.remarks}
                onChange={e => setStopForm({ ...stopForm, remarks: e.target.value })}
                className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm min-h-[60px]"
                data-testid="admin-extra-stop-remarks" />
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button onClick={() => setStopDialog(null)}
                  className="py-2 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] rounded-full"
                  data-testid="admin-extra-stop-cancel">Cancel</button>
                <button onClick={submitExtraStop}
                  className="py-2 text-sm font-bold uppercase tracking-widest bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-full"
                  data-testid="admin-extra-stop-save">Add Stop</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Phase 76w — Trip edit history accordion. Renders every entry in
// `trip.edit_log` (set by `PUT /api/trips/{id}/edit`) so admins can audit
// who changed which field, when, and what the old/new values were.
function EditHistoryAccordion({ entries }) {
  const [open, setOpen] = useState(false);
  if (!entries || entries.length === 0) return null;

  const fmtVal = (v) => {
    if (v == null || v === '') return '—';
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string') return v.length > 60 ? v.slice(0, 60) + '…' : v;
    try { return JSON.stringify(v).slice(0, 60); } catch { return String(v); }
  };
  // Show newest first.
  const sorted = [...entries].sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  return (
    <div className="mt-3 border border-[#E5E5E5]" data-testid="edit-history-accordion">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 flex items-center justify-between bg-[#F8F8F8] hover:bg-[#F0F0F0] text-left"
        data-testid="edit-history-toggle"
      >
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#666]">
          Edit History · {entries.length} change{entries.length > 1 ? 's' : ''}
        </span>
        <span className="text-xs text-[#999]">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-3 space-y-1.5 text-xs max-h-72 overflow-y-auto">
          {sorted.map((e, i) => (
            <div key={i} className="border-l-2 border-[#002FA7] pl-2 py-1" data-testid={`edit-log-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold uppercase tracking-widest text-[10px] text-[#002FA7]">
                  {(e.field || '').replace(/_/g, ' ')}
                </span>
                <span className="text-[10px] text-[#999]">{fmtTime(e.at)} · by {e.by || '—'}</span>
              </div>
              <div className="mt-0.5 text-[11px]">
                <span className="text-[#FF3333] line-through">{fmtVal(e.old)}</span>
                <span className="mx-1 text-[#999]">→</span>
                <span className="text-[#00C853] font-bold">{fmtVal(e.new)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Phase 76m — Pool decision history accordion. Lazy-loads `/trips/{id}/pool-history`
// on first expand. Renders the full audit trail: who got the auto-attached
// offer, when, the lead trip relationship (if any), and the bookings merged
// onto this trip when it's the pool lead.
function PoolHistoryAccordion({ tripId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (data) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/trips/${tripId}/pool-history`);
      setData(d);
    } catch {
      setData({ offers: [], pooled_into: [], lead: null });
    } finally { setLoading(false); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const hasActivity = data && (data.offers.length > 0 || data.pooled_into.length > 0 || data.lead);

  return (
    <div className="mt-3 border border-[#E5E5E5]" data-testid="pool-history-accordion">
      <button
        type="button"
        onClick={toggle}
        className="w-full px-3 py-2 flex items-center justify-between bg-[#F8F8F8] hover:bg-[#F0F0F0] text-left"
        data-testid="pool-history-toggle"
      >
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Pool History</span>
        <span className="text-xs text-[#999]">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-3 space-y-2 text-xs">
          {loading && <p className="text-[#999]">Loading…</p>}
          {!loading && data && !hasActivity && (
            <p className="text-[#999]" data-testid="pool-history-empty">No pool activity for this trip.</p>
          )}
          {!loading && data && data.lead && (
            <div className="bg-[#A855F7]/5 border border-[#A855F7]/20 p-2 rounded">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#A855F7] mb-1">Pooled Into Lead</p>
              <p>#{(data.lead.id || '').slice(0, 8)} · {data.lead.pickup_supplier_name || '-'} → {data.lead.drop_supplier_name || '-'}</p>
              <p className="text-[#666] mt-0.5">Pilot: {data.lead.pilot_name || '-'} · Veh: {data.lead.vehicle_number || '-'} · Status: {data.lead.status || '-'}</p>
            </div>
          )}
          {!loading && data && data.offers.length > 0 && (
            <div data-testid="pool-history-offers">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] mb-1">Decision Trail</p>
              <div className="space-y-1">
                {data.offers.map((o, i) => (
                  <div key={o.id || i} className="flex items-center justify-between bg-[#F3F3F3] px-2 py-1 rounded" data-testid={`pool-offer-row-${i}`}>
                    <div>
                      <span className="font-bold">{o.candidate_pilot_name || '-'}</span>
                      <span className="text-[#666] ml-2">{o.distance_km != null ? `${o.distance_km} km` : '-'}</span>
                      {o.eta_min ? <span className="text-[#666] ml-1">· {o.eta_min}m drive</span> : null}
                    </div>
                    <span
                      className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                        o.status === 'accepted' || o.status === 'auto_attached' ? 'bg-[#00C853]/10 text-[#00C853]' :
                        o.status === 'rejected' ? 'bg-[#FF3333]/10 text-[#FF3333]' :
                        o.status === 'expired' ? 'bg-[#FF8C00]/10 text-[#FF8C00]' :
                        'bg-[#808080]/10 text-[#666]'
                      }`}
                    >
                      {o.status === 'auto_attached' ? 'Auto-attached' : o.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!loading && data && data.pooled_into.length > 0 && (
            <div data-testid="pool-history-pooled-into">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#A855F7] mb-1">Bookings Merged Into This Trip</p>
              <div className="space-y-1">
                {data.pooled_into.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between bg-[#A855F7]/5 px-2 py-1 rounded">
                    <div>
                      <span>{p.item || '-'} · {p.qty || 0} {p.uom || ''}</span>
                      <span className="text-[#666] ml-2">by {p.booked_by_name || '-'}</span>
                    </div>
                    <span className="text-[9px] uppercase tracking-widest font-bold text-[#666]">{p.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Phase 76l — Admin edit-trip dialog. Lets admin patch the safe editable
// fields (route, item/qty, vehicle types, priority, remarks, ready time)
// of any non-cancelled trip — including pooled stubs and pool leads.
function EditTripDialog({ trip, onClose, onSaved }) {
  const [form, setForm] = useState({
    pickup_supplier_name: trip.pickup_supplier_name || '',
    drop_supplier_name: trip.drop_supplier_name || '',
    pickup_contact: trip.pickup_contact || '',
    drop_contact: trip.drop_contact || '',
    item: trip.item || '',
    qty: trip.qty ?? '',
    uom: trip.uom || '',
    priority: trip.priority || 'others',
    remarks: trip.remarks || '',
    vehicle_type_1: trip.vehicle_type_1 || '',
    vehicle_type_2: trip.vehicle_type_2 || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (payload.qty !== '' && payload.qty != null) payload.qty = Number(payload.qty);
      await api.put(`/trips/${trip.id}/edit`, payload);
      await onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Edit failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="edit-trip-dialog">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold uppercase tracking-widest">Edit Trip · #{trip.id?.slice(0, 8)}</h3>
          <button onClick={onClose} className="text-[#666] hover:text-[#0A0A0A]">
            <X size={18} />
          </button>
        </div>
        {error && <p className="text-xs text-[#FF3333] bg-[#FF3333]/10 p-2">{error}</p>}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Pickup Supplier</label>
            <input value={form.pickup_supplier_name} onChange={update('pickup_supplier_name')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-pickup-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Pickup Contact</label>
            <input value={form.pickup_contact} onChange={update('pickup_contact')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-pickup-contact" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Drop Supplier</label>
            <input value={form.drop_supplier_name} onChange={update('drop_supplier_name')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-drop-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Drop Contact</label>
            <input value={form.drop_contact} onChange={update('drop_contact')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-drop-contact" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Item</label>
            <input value={form.item} onChange={update('item')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-item" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Qty</label>
            <input type="number" value={form.qty} onChange={update('qty')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-qty" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">UOM</label>
            <input value={form.uom} onChange={update('uom')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-uom" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Vehicle Type 1</label>
            <input value={form.vehicle_type_1} onChange={update('vehicle_type_1')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-vt1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Vehicle Type 2</label>
            <input value={form.vehicle_type_2} onChange={update('vehicle_type_2')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-vt2" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Priority</label>
            <select value={form.priority} onChange={update('priority')} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-priority">
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="others">Others</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#666]">Remarks</label>
            <textarea value={form.remarks} onChange={update('remarks')} rows={2} className="w-full border border-[#E5E5E5] px-2 py-1.5" data-testid="edit-remarks" />
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="text-xs uppercase tracking-widest px-4 py-2 border border-[#E5E5E5]" data-testid="edit-trip-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="text-xs uppercase tracking-widest px-4 py-2 bg-[#002FA7] text-white disabled:opacity-50" data-testid="edit-trip-save">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== MAIN TRIP MONITORING =====
export default function TripMonitoring() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({ pilot_name: '', vehicle_number: '', from_date: '', to_date: '', allocation_mode: '' });
  const [allocateDialog, setAllocateDialog] = useState(null);
  // Phase 78g — after a pilot is picked in the allocate dialog, this
  // holds the confirm payload for the AllocateConfirmModal (queue +
  // standalone/pool choice).
  const [confirmAllocate, setConfirmAllocate] = useState(null);
  const [selectedPilot, setSelectedPilot] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailTripId, setDetailTripId] = useState(null);
  // Phase 77d (Feb-2026) — Live Map modal. Distinct from detail panel:
  // it auto-refreshes every 10s and is dedicated to LIVE trips so admin
  // can see exactly where the vehicle is + ETA + route map.
  const [liveMapTripId, setLiveMapTripId] = useState(null);

  // Phase 76y (Feb-2026) — per-column filter values + sort. Keys map to
  // the `colDefs` array below. Empty filter = match-all. Sort dir null
  // means "default order from server".
  const [colFilters, setColFilters] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const onSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };
  // Accessor helpers — `get*` returns plain string/number used for filter
  // matching and sort comparison. Display logic in each <TableCell> stays
  // visual-rich; this is the canonical "value" of each cell.
  const itemText = (t) => Array.isArray(t.items) && t.items.length > 0
    ? t.items.map(it => `${it.name} ${it.qty}${it.uom || ''}`).join(' · ')
    : t.item ? `${t.item} ${t.qty || 0} ${t.uom || ''}` : '';
  const colDefs = useMemo(() => {
    const base = [
      // {key, label, get(t), sortable, filterable, type}
      { key: 'id',           label: 'ID',           get: t => t.id || '',                  sortable: true,  filterable: true },
      { key: 'pickup_flow',  label: 'Pickup Flow',  get: t => t.pickup_flow ? (t.dc_ready ? 'dc ready' : 'awaiting dc') : '', sortable: false, filterable: false },
      { key: 'pickup',       label: 'Pickup',       get: t => t.pickup_supplier_name || t.pickup_location || '', sortable: true, filterable: true },
      { key: 'drop',         label: 'Drop',         get: t => t.drop_supplier_name || t.drop_location || '',     sortable: true, filterable: true },
      { key: 'item',         label: 'Item',         get: itemText,                          sortable: true,  filterable: true },
      { key: 'pilot',        label: 'Pilot',        get: t => t.pilot_name || '',           sortable: true,  filterable: true },
      { key: 'vehicle',      label: 'Vehicle',      get: t => t.vehicle_number || '',       sortable: true,  filterable: true },
      { key: 'helpers',      label: 'Helpers',      get: t => (t.loadmen?.length || 0),     sortable: true,  filterable: true, type: 'number' },
      { key: 'priority',     label: 'Priority',     get: t => t.priority || 'others',       sortable: true,  filterable: true },
      { key: 'status',       label: 'Status',       get: t => t.status || '',               sortable: true,  filterable: true },
      { key: 'booked',       label: 'Booked',       get: t => t.created_at || '',           sortable: true,  filterable: true, type: 'date' },
      { key: 'booked_by',    label: 'Booked By',    get: t => t.booked_by_name || '',       sortable: true,  filterable: true },
      { key: 'booked_from',  label: 'Booking From', get: t => t.booked_from_halt || '',     sortable: true,  filterable: true },
      { key: 'allocation',   label: 'Allocation',   get: t => (t.preferred_pilot_id || t.preferred_pilot_name) ? 'preferred' : (t.allocation_mode || (t.allocation_type === 'manual' ? 'manual' : 'auto')), sortable: true, filterable: true },
    ];
    // Phase 76z — In the Cancelled tab only, append Cancelled By column.
    if (activeTab === 'cancelled') {
      base.push({ key: 'cancelled_by', label: 'Cancelled By', get: t => t.cancelled_by || '', sortable: true, filterable: true });
    }
    return base;
  }, [activeTab]);

  // Apply per-column filters → sort → render.
  const visibleTrips = useMemo(() => {
    let rows = trips.filter(t => {
      // Existing allocation_mode global filter (top section)
      if (filters.allocation_mode) {
        // Phase 78ay — Preferred-pilot trips bucket separately.
        const isPreferred = Boolean(t.preferred_pilot_id || t.preferred_pilot_name);
        const mode = isPreferred
          ? 'preferred'
          : (t.allocation_mode || (t.allocation_type === 'manual' ? 'manual' : 'auto'));
        if (mode !== filters.allocation_mode) return false;
      }
      // Per-column filters.
      for (const cd of colDefs) {
        const q = (colFilters[cd.key] || '').toString().trim().toLowerCase();
        if (!q || !cd.filterable) continue;
        const val = String(cd.get(t) ?? '').toLowerCase();
        if (!val.includes(q)) return false;
      }
      return true;
    });
    if (sortKey) {
      const cd = colDefs.find(c => c.key === sortKey);
      if (cd) {
        const cmp = (a, b) => {
          const av = cd.get(a), bv = cd.get(b);
          if (cd.type === 'number') return (Number(av) || 0) - (Number(bv) || 0);
          if (cd.type === 'date') return String(av || '').localeCompare(String(bv || ''));
          return String(av || '').toLowerCase().localeCompare(String(bv || '').toLowerCase());
        };
        rows = [...rows].sort((a, b) => sortDir === 'asc' ? cmp(a, b) : cmp(b, a));
      }
    }
    return rows;
  }, [trips, filters.allocation_mode, colFilters, sortKey, sortDir, colDefs]);

  useEffect(() => { fetchTrips(); fetchPilots(); }, [activeTab]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const params = {};
      // Phase 78aw — Split Allocated/In Progress tabs by Auto vs Manual allocation.
      // Phase 78ay — Added Preferred-pilot bucket (Pending / Allocated / In Progress).
      // A trip with a `preferred_pilot_id` (or legacy `preferred_pilot_name`) goes
      // ONLY into the Preferred bucket — it is excluded from Auto, Manual, and the
      // generic Pending tab so each trip lives in exactly one bucket.
      let tabStatus = activeTab;
      if (activeTab === 'allocated_auto' || activeTab === 'allocated_manual' || activeTab === 'allocated_preferred') tabStatus = 'allocated';
      else if (activeTab === 'in_progress_auto' || activeTab === 'in_progress_manual' || activeTab === 'in_progress_preferred') tabStatus = 'in_progress';
      else if (activeTab === 'pending_preferred') tabStatus = 'pending';
      if (tabStatus !== 'all') params.status = tabStatus;
      if (filters.pilot_name) params.pilot_name = filters.pilot_name;
      if (filters.vehicle_number) params.vehicle_number = filters.vehicle_number;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const { data } = await api.get('/trips/all', { params });

      // Bucket helper — preferred wins over auto/manual.
      const isPreferred = (t) => Boolean(t.preferred_pilot_id || t.preferred_pilot_name);
      const allocBucket = (t) => {
        if (isPreferred(t)) return 'preferred';
        // Legacy fallback: preferred_pilot_name USED to imply manual. Now that we
        // have a dedicated preferred bucket, only honour `allocation_mode` /
        // `allocation_type` for the auto-vs-manual split.
        return t.allocation_mode || (t.allocation_type === 'manual' ? 'manual' : 'auto');
      };

      let rows = data;
      const wantAuto = activeTab.endsWith('_auto');
      const wantManual = activeTab.endsWith('_manual');
      const wantPreferred = activeTab.endsWith('_preferred');
      if (wantAuto || wantManual || wantPreferred) {
        const wantBucket = wantPreferred ? 'preferred' : wantAuto ? 'auto' : 'manual';
        rows = data.filter(t => allocBucket(t) === wantBucket);
      } else if (activeTab === 'pending') {
        // Generic "Pending" tab — exclude preferred-pilot trips (they have their
        // own "Preferred Pending" tab).
        rows = data.filter(t => !isPreferred(t));
      }
      setTrips(rows);
    } catch {} finally { setLoading(false); }
  };

  const fetchPilots = async () => {
    try { const { data } = await api.get('/admin/pilots'); setPilots(data); } catch {}
  };

  const handleAllocate = async () => {
    if (!selectedPilot || !allocateDialog) return;
    // Phase 78g — instead of allocating immediately, open the confirm
    // modal which shows the pilot's queue + lets admin choose
    // standalone vs pool-into.
    const pilot = pilots.find(p => p.id === selectedPilot);
    setConfirmAllocate({
      trip: allocateDialog,
      pilotId: selectedPilot,
      pilotName: pilot?.name || '',
    });
    setAllocateDialog(null);
  };

  const handleCancel = async (tripId) => {
    if (!window.confirm('Cancel this trip?')) return;
    await api.put(`/trips/${tripId}/cancel`);
    fetchTrips();
  };

  const handlePushAuto = async (tripId) => {
    await api.put(`/trips/${tripId}/push-auto`);
    fetchTrips();
  };

  return (
    <div data-testid="trip-monitoring" className="space-y-6">
      {/* Trip Detail Panel */}
      {detailTripId && <TripDetailPanel tripId={detailTripId} onClose={() => setDetailTripId(null)} currentUser={user} />}

      {/* Live Trip Map Modal (auto-refresh, dedicated to live trips) */}
      {liveMapTripId && <LiveTripMapModal tripId={liveMapTripId} onClose={() => setLiveMapTripId(null)} />}

      {/* Phase 78g — Allocate/Reassign confirm step. Shown after admin
          picks a pilot in the allocate dialog. Reveals the pilot's
          current queue and lets admin choose standalone vs pool-into. */}
      <AllocateConfirmModal
        open={!!confirmAllocate}
        trip={confirmAllocate?.trip}
        pilotId={confirmAllocate?.pilotId}
        pilotName={confirmAllocate?.pilotName}
        onBack={() => { setAllocateDialog(confirmAllocate.trip); setConfirmAllocate(null); }}
        onClose={() => { setConfirmAllocate(null); setSelectedPilot(''); }}
        onDone={() => { setConfirmAllocate(null); setSelectedPilot(''); fetchTrips(); }}
      />

      <div>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Trip Monitoring</h1>
        <p className="text-[#666] text-sm mt-1">Click any trip row to see full details with map route</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div><Label className="text-xs uppercase tracking-widest">Pilot Name</Label><Input value={filters.pilot_name} onChange={e => setFilters({...filters, pilot_name: e.target.value})} className="rounded-none" placeholder="Filter..." /></div>
          <div><Label className="text-xs uppercase tracking-widest">Vehicle Number</Label><Input value={filters.vehicle_number} onChange={e => setFilters({...filters, vehicle_number: e.target.value})} className="rounded-none" placeholder="Filter..." /></div>
          <div><Label className="text-xs uppercase tracking-widest">From Date</Label><Input type="date" value={filters.from_date} onChange={e => setFilters({...filters, from_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">To Date</Label><Input type="date" value={filters.to_date} onChange={e => setFilters({...filters, to_date: e.target.value})} className="rounded-none" /></div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Allocation</Label>
            <select
              value={filters.allocation_mode}
              onChange={e => setFilters({...filters, allocation_mode: e.target.value})}
              data-testid="filter-allocation-mode"
              className="w-full h-10 px-3 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7]"
            >
              <option value="">All</option>
              <option value="auto">Auto only</option>
              <option value="manual">Manual only</option>
              <option value="preferred">Preferred only</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-3 gap-2">
          <ExportButton rows={trips} columns={TRIP_EXPORT_COLUMNS} filename="trips" testId="export-trips-btn" />
          <Button onClick={fetchTrips} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="filter-trips-btn">Apply Filters</Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none bg-[#F3F3F3] border border-[#E5E5E5] flex flex-wrap h-auto justify-start">
          <TabsTrigger value="all" className="rounded-none text-xs uppercase tracking-widest">All</TabsTrigger>
          <TabsTrigger value="awaiting_goods_ready" className="rounded-none text-xs uppercase tracking-widest">Awaiting Goods</TabsTrigger>
          <TabsTrigger value="pending" className="rounded-none text-xs uppercase tracking-widest">Pending</TabsTrigger>
          <TabsTrigger value="pending_preferred" data-testid="tab-pending-preferred" className="rounded-none text-xs uppercase tracking-widest">Preferred Pending</TabsTrigger>
          <TabsTrigger value="allocated_auto" data-testid="tab-allocated-auto" className="rounded-none text-xs uppercase tracking-widest">Auto Allocated</TabsTrigger>
          <TabsTrigger value="allocated_manual" data-testid="tab-allocated-manual" className="rounded-none text-xs uppercase tracking-widest">Manual Allocated</TabsTrigger>
          <TabsTrigger value="allocated_preferred" data-testid="tab-allocated-preferred" className="rounded-none text-xs uppercase tracking-widest">Preferred Allocated</TabsTrigger>
          <TabsTrigger value="in_progress_auto" data-testid="tab-in-progress-auto" className="rounded-none text-xs uppercase tracking-widest">Auto In Progress</TabsTrigger>
          <TabsTrigger value="in_progress_manual" data-testid="tab-in-progress-manual" className="rounded-none text-xs uppercase tracking-widest">Manual In Progress</TabsTrigger>
          <TabsTrigger value="in_progress_preferred" data-testid="tab-in-progress-preferred" className="rounded-none text-xs uppercase tracking-widest">Preferred In Progress</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-none text-xs uppercase tracking-widest">Completed</TabsTrigger>
          <TabsTrigger value="cancelled" className="rounded-none text-xs uppercase tracking-widest">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Allocate / Re-assign Dialog */}
      <Dialog open={!!allocateDialog} onOpenChange={() => setAllocateDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {allocateDialog?.status === 'pending' ? 'Allocate Trip to Pilot' : `Change Pilot (currently ${allocateDialog?.pilot_name || 'none'})`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm">Trip: <strong>#{allocateDialog?.id?.slice(0,8)}</strong> · Status: <span className="uppercase tracking-widest text-xs">{allocateDialog?.status}</span></p>
            <p className="text-sm">{allocateDialog?.pickup_supplier_name} → {allocateDialog?.drop_supplier_name}</p>
            <div>
              <Label className="text-xs uppercase tracking-widest">Select Pilot</Label>
              <Select value={selectedPilot} onValueChange={setSelectedPilot}>
                <SelectTrigger className="rounded-none" data-testid="manual-allocate-pilot-trigger"><SelectValue placeholder="Choose pilot" /></SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {pilots
                    .filter(p => p.status === 'active' && p.id !== allocateDialog?.pilot_id)
                    // Pilots WITH a scanned vehicle bubble to the top of the list
                    // so admin can pick one in a single glance.
                    .sort((a, b) => {
                      const aHas = !!a.current_vehicle_number;
                      const bHas = !!b.current_vehicle_number;
                      if (aHas !== bHas) return aHas ? -1 : 1;
                      return (a.name || '').localeCompare(b.name || '');
                    })
                    .map(p => (
                      <SelectItem key={p.id} value={p.id} data-testid={`manual-allocate-pilot-${p.id}`}>
                        <div className="flex items-center gap-2 w-full">
                          <span className="flex-1 truncate">{p.name} {p.emp_id ? <span className="text-[#666]">({p.emp_id})</span> : null}</span>
                          {p.current_vehicle_number ? (
                            <span className="inline-flex items-center gap-1 bg-[#0A0A0A] text-white px-1.5 py-0.5 font-mono text-[10px] flex-shrink-0">
                              {p.current_vehicle_number}
                              {p.current_vehicle_body_type && (
                                <span className="text-[9px] text-white/60 normal-case">· {p.current_vehicle_body_type}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-[9px] text-[#FF3333] uppercase tracking-widest flex-shrink-0">No vehicle</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAllocateDialog(null)} className="rounded-none">Cancel</Button>
              <Button onClick={handleAllocate} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="confirm-allocate-btn">
                {allocateDialog?.status === 'pending' ? 'Allocate' : 'Re-assign'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trip Table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader>
            {/* Header row — sortable column titles. Click to toggle asc → desc → off. */}
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest w-12">S.No</TableHead>
              {colDefs.map(cd => (
                <TableHead
                  key={cd.key}
                  className={`text-xs uppercase tracking-widest ${cd.sortable ? 'cursor-pointer select-none' : ''}`}
                  onClick={cd.sortable ? () => onSort(cd.key) : undefined}
                  data-testid={`th-${cd.key}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {cd.label}
                    {cd.sortable && (
                      <span className="text-[10px] text-[#666] w-3">
                        {sortKey === cd.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    )}
                  </span>
                </TableHead>
              ))}
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
            {/* Per-column filter row — Excel-style. */}
            <TableRow className="bg-white">
              <TableHead className="py-1 w-12">
                <span className="text-[10px] text-[#999]">{visibleTrips.length}</span>
              </TableHead>
              {colDefs.map(cd => (
                <TableHead key={`f-${cd.key}`} className="py-1">
                  {cd.filterable ? (
                    <input
                      data-testid={`filter-${cd.key}`}
                      value={colFilters[cd.key] || ''}
                      onChange={e => setColFilters(f => ({ ...f, [cd.key]: e.target.value }))}
                      placeholder="Filter…"
                      className="w-full text-xs px-2 py-1 border border-[#E5E5E5] bg-white"
                    />
                  ) : <span className="text-[10px] text-[#999]">—</span>}
                </TableHead>
              ))}
              <TableHead className="py-1 text-right">
                {Object.values(colFilters).some(v => (v || '').trim()) && (
                  <button
                    type="button"
                    onClick={() => setColFilters({})}
                    className="text-[10px] uppercase tracking-widest text-[#FF3333] underline"
                    data-testid="clear-col-filters-btn"
                  >Clear</button>
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleTrips.map((t, i) => (
              <TableRow
                key={t.id}
                className={`cursor-pointer transition-colors hover:bg-[#002FA7]/5 ${t.is_pooled_into ? 'bg-[#A855F7]/5' : (i % 2 === 0 ? '' : 'bg-[#F3F3F3]/30')}`}
                onClick={() => setDetailTripId(t.id)}
                data-testid={`trip-row-${t.id}`}
              >
                <TableCell className="text-xs text-[#666] font-mono" data-testid={`sno-${t.id}`}>{i + 1}</TableCell>
                <TableCell className="font-mono text-xs">
                  {t.id?.slice(0, 8)}
                  {t.is_pooled_into && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest bg-[#A855F7]/10 text-[#A855F7] px-1 py-0.5 rounded font-bold" title={`Pooled with #${t.is_pooled_into}`}>
                      Pooled
                    </span>
                  )}
                  {Array.isArray(t.pooled_items) && t.pooled_items.length > 0 && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest bg-[#A855F7]/10 text-[#A855F7] px-1 py-0.5 rounded font-bold" title={`This trip carries ${t.pooled_items.length} pooled bookings`}>
                      +{t.pooled_items.length} pool
                    </span>
                  )}
                  {t.capacity_exceeded && (
                    <span
                      className="ml-1 text-[9px] uppercase tracking-widest bg-[#FF3333] text-white px-1 py-0.5 rounded font-bold animate-pulse"
                      title={t.capacity_warning || 'Total qty exceeds vehicle capacity. Split the pool or change the vehicle.'}
                      data-testid={`capacity-badge-${t.id}`}
                    >
                      ⚠ Capacity
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {t.pickup_flow ? (
                    (() => {
                      const chipCls = t.dc_ready ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FF8C00]/10 text-[#FF8C00]';
                      const label = t.status === 'pending' && !t.dc_ready ? 'Awaiting DC' : (t.dc_ready ? 'DC Ready' : 'Awaiting DC');
                      return (
                        <span
                          className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 ${chipCls}`}
                          title={`Pickup: ${t.pickup_user_name || '-'} (${t.pickup_user_mobile || '-'})${t.dc_ready_at ? ' · Ready at ' + new Date(t.dc_ready_at).toLocaleString() : ''}`}
                          data-testid={`pickup-flow-${t.id}`}
                        >
                          🏭 {label}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="text-[#999] text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="min-w-[180px] max-w-[260px] whitespace-normal align-top">
                  <span className="text-sm font-medium leading-tight block">{t.pickup_supplier_name || t.pickup_location}</span>
                  {t.trip_type && (
                    <span className={`mt-1 inline-block text-[9px] px-1 py-0.5 uppercase tracking-widest ${t.trip_type === 'back_to_office' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#FF8C00]/10 text-[#FF8C00]'}`}>
                      {t.trip_type === 'back_to_office' ? 'RTO' : 'W/O V'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="min-w-[180px] max-w-[260px] whitespace-normal align-top">
                  <span className="text-sm font-medium leading-tight block">{t.drop_supplier_name || t.drop_location}</span>
                </TableCell>
                <TableCell className="min-w-[140px] max-w-[200px] whitespace-normal align-top" data-testid={`trip-${t.id}-item`}>
                  {Array.isArray(t.items) && t.items.length > 0 ? (
                    <span className="text-xs leading-tight block">{t.items.map(it => `${it.name} ${it.qty}${it.uom || ''}`).join(' · ')}</span>
                  ) : t.item ? (
                    <span className="text-xs leading-tight block">{t.item} <span className="text-[#666]">· {t.qty || 0} {t.uom || ''}</span></span>
                  ) : (
                    <span className="text-[#999] text-xs">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1">
                    {t.pilot_name || '-'}
                    <ManualBadge mode={t.pilot_allocation_mode} />
                  </span>
                </TableCell>
                <TableCell>{t.vehicle_number || '-'}</TableCell>
                <TableCell className="text-right">
                  {Array.isArray(t.loadmen) && t.loadmen.length > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold"
                      title={t.loadmen.map(l => `${l.name}${l.emp_id ? ` (${l.emp_id})` : ''}`).join(', ')}
                    >
                      {t.loadmen.length}
                    </span>
                  ) : (
                    <span className="text-[#999] text-xs">0</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${t.priority === 'shortage' ? 'bg-[#FF3333]/10 text-[#FF3333]' : t.priority === 'qr' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#808080]/10 text-[#808080]'}`}>{t.priority || 'others'}</span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${STATUS_BADGE[t.status] || ''}`}>
                    {t.status === 'awaiting_goods_ready' ? 'Awaiting Goods' : t.status}
                  </span>
                  {/* Phase 78aw — alongside the status pill, show the
                      time that status was entered (12-hour IST) so admins
                      can see "Pickup Reached 11:30am" at a glance. We
                      prefer the Google-derived timestamp (more reliable
                      since Phase 78at) and fall back to the manual one. */}
                  {(() => {
                    const map = {
                      in_progress: t.google_start_time || t.started_at,
                      pickup_reached: t.google_pickup_reach_time || t.pickup_reached_at,
                      pickup_done: t.google_pickup_complete_time || t.pickup_done_at,
                      drop_reached: t.google_drop_reach_time || t.drop_reached_at,
                      drop_done: t.google_drop_complete_time || t.drop_done_at,
                      completed: t.completed_at,
                      cancelled: t.cancelled_at,
                      allocated: t.allocated_at,
                      pooled: t.pooled_at,
                    };
                    const iso = map[t.status];
                    if (!iso) return null;
                    let s = '';
                    try {
                      s = new Date(iso.includes('T') ? iso : iso + 'Z').toLocaleTimeString('en-IN', {
                        hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
                      });
                    } catch { return null; }
                    return (
                      <div className="text-[10px] font-mono text-[#0A0A0A] mt-1 font-bold" data-testid={`trip-${t.id}-status-time`}>
                        {s}
                      </div>
                    );
                  })()}
                  {t.status === 'awaiting_goods_ready' && t.material_ready_datetime && (
                    <div className="text-[10px] text-[#A855F7] mt-1">
                      Ready @ {new Date(t.material_ready_datetime.includes('T') ? t.material_ready_datetime : t.material_ready_datetime + 'Z').toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                    </div>
                  )}
                  {t.status === 'pending' && t.queue_position && (
                    <span className="ml-1 text-[10px] uppercase tracking-widest px-1.5 py-1 bg-[#FF8C00]/10 text-[#FF8C00] font-bold">
                      Q#{t.queue_position}
                    </span>
                  )}
                  {t.allocation_type === 'auto_bunched' && (
                    <span
                      className="ml-1 text-[10px] uppercase tracking-widest px-1.5 py-1 bg-[#A855F7]/10 text-[#A855F7] font-bold"
                      title={`Bunched with another pickup at ${t.pickup_supplier_name || 'same supplier'}`}
                      data-testid={`trip-${t.id}-bunched`}
                    >
                      ✚ Bunched
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {t.created_at ? (
                    <div className="flex flex-col leading-tight">
                      <span className="text-xs">{fmtTime(t.created_at)}</span>
                      <span
                        className="text-[10px] text-[#666]"
                        data-testid={`booked-ago-${t.id}`}
                        title={new Date(t.created_at).toLocaleString('en-IN')}
                      >
                        {fmtAgo(t.created_at)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-[#999] text-xs">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{t.booked_by_name || '-'}</span>
                    {t.booked_by_mobile && (
                      <a
                        href={`tel:${t.booked_by_mobile}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] font-mono text-[#002FA7] bg-[#002FA7]/10 px-1.5 py-0.5 rounded hover:bg-[#002FA7]/20 whitespace-nowrap"
                        data-testid={`booker-mobile-${t.id}`}
                      >
                        {t.booked_by_mobile}
                      </a>
                    )}
                  </div>
                </TableCell>
                {/* Phase 76y — Booking From = booker's halt location */}
                <TableCell data-testid={`booked-from-${t.id}`}>
                  {t.booked_from_halt
                    ? <span className="text-[10px] uppercase tracking-widest bg-[#8E44AD]/10 text-[#8E44AD] px-2 py-1">{t.booked_from_halt}</span>
                    : <span className="text-[#999] text-xs">—</span>}
                </TableCell>
                <TableCell>
                  {(() => {
                    // Phase 78ay — 3-bucket allocation: Preferred (preferred-pilot
                    // bookings) > Manual (admin override) > Auto (system pick).
                    // Pre-Phase-32 trips had allocation_type='manual' only on
                    // admin reassigns.
                    const isPreferred = Boolean(t.preferred_pilot_id || t.preferred_pilot_name);
                    const mode = isPreferred
                      ? 'preferred'
                      : (t.allocation_mode || (t.allocation_type === 'manual' ? 'manual' : 'auto'));
                    const styleMap = {
                      preferred: 'bg-[#8E44AD]/10 text-[#8E44AD]',
                      manual:    'bg-[#FF8C00]/10 text-[#FF8C00]',
                      auto:      'bg-[#00C853]/10 text-[#00C853]',
                    };
                    const tip = mode === 'preferred'
                      ? `Preferred pilot booking${t.preferred_pilot_name ? `: ${t.preferred_pilot_name}` : ''}`
                      : mode === 'manual'
                        ? (t.manual_allocated_by ? `Manually reassigned by ${t.manual_allocated_by}` : 'Manually allocated')
                        : 'Auto-allocated by the system';
                    return (
                      <span
                        data-testid={`allocation-mode-${t.id}`}
                        title={tip}
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 ${styleMap[mode]}`}
                      >
                        {mode}
                      </span>
                    );
                  })()}
                </TableCell>
                {/* Phase 76z — Cancelled By column, only visible in cancelled tab */}
                {activeTab === 'cancelled' && (
                  <TableCell data-testid={`cancelled-by-${t.id}`}>
                    {t.cancelled_by ? (
                      <div>
                        <span className="text-xs font-bold">{t.cancelled_by}</span>
                        {t.cancelled_by_role && (
                          <span className="ml-1 text-[9px] uppercase tracking-widest text-[#666]">({t.cancelled_by_role.replace('_', ' ')})</span>
                        )}
                        {t.cancelled_at && (
                          <div className="text-[10px] text-[#999]">{fmtTime(t.cancelled_at)}</div>
                        )}
                      </div>
                    ) : <span className="text-[#999] text-xs">—</span>}
                  </TableCell>
                )}
                <TableCell className="text-right space-x-1" onClick={e => e.stopPropagation()}>
                  {/* Live Map — visible on any live trip; opens a dedicated
                      auto-refreshing map modal with pilot live position +
                      pickup/drop + ETA + route polyline. */}
                  {LIVE_TRIP_STATUSES.has(t.status) && (
                    <button
                      onClick={() => setLiveMapTripId(t.id)}
                      className="p-1 hover:bg-[#00C853]/10 text-[#00C853]"
                      title="View Live Map"
                      data-testid={`view-live-map-${t.id}`}
                    >
                      <MapTrifold size={16} weight="fill" />
                    </button>
                  )}
                  {t.status === 'awaiting_goods_ready' && (
                    <>
                      <button onClick={() => setAllocateDialog(t)} className="p-1 hover:bg-[#002FA7]/10 text-[#002FA7]" title="Force Allocate (skip wait)" data-testid={`allocate-trip-${t.id}`}><User size={16} /></button>
                      <button onClick={() => handlePushAuto(t.id)} className="p-1 hover:bg-[#A855F7]/10 text-[#A855F7]" title="Force to Auto-Allocate (skip wait)" data-testid={`force-push-${t.id}`}><ArrowsClockwise size={16} /></button>
                    </>
                  )}
                  {t.status === 'pending' && (
                    <>
                      <button onClick={() => setAllocateDialog(t)} className="p-1 hover:bg-[#002FA7]/10 text-[#002FA7]" title="Manual Allocate" data-testid={`allocate-trip-${t.id}`}><User size={16} /></button>
                      <button onClick={() => handlePushAuto(t.id)} className="p-1 hover:bg-[#00C853]/10 text-[#00C853]" title="Auto Allocate"><ArrowsClockwise size={16} /></button>
                    </>
                  )}
                  {(t.status === 'allocated' || t.status === 'accepted') && (
                    <button onClick={() => setAllocateDialog(t)} className="p-1 hover:bg-[#FF8C00]/10 text-[#FF8C00]" title="Change Pilot" data-testid={`reassign-trip-${t.id}`}><ArrowsClockwise size={16} /></button>
                  )}
                  {['awaiting_goods_ready','pending','allocated','accepted'].includes(t.status) && (
                    <button onClick={() => handleCancel(t.id)} className="p-1 hover:bg-[#FF3333]/10 text-[#FF3333]" title="Cancel"><XCircle size={16} /></button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {visibleTrips.length === 0 && <TableRow><TableCell colSpan={colDefs.length + 2} className="text-center text-[#666] py-8">{loading ? 'Loading...' : 'No trips match your filters'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
